// UPDATED: Added clarification paragraphs directly after penetration tables
// for eTOD and ICAO Doc 015 (before any 'no penetration' tables).
// Uses translation keys: etodClarification, techEquipmentClarification.
// UPDATED 2: Table section headings (penetration / no penetration) now bold.

import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import { savePdfBlob } from "./savePdfBlob";
import { getNavAidTypeLabel } from "./navIcons";
import { buildSummaryRowsFromAnalysis } from "./buildSummaryRows";
import { stripPenetrationStylingForPDF } from "./highlightPenetrationPhrases";
import { appendGnssApproachReport } from "./gnssApproachPdf";
import { appendLocApproachReport } from './locApproachPdf';
import { createPdfCommentHooks } from './pdfCommentStyles';

/**
 * Hjälpfunktioner
 */
function addTitleWithOffset(doc, text, x, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(text, x, y);
  return y + 2;
}

function addTextWithOffset(doc, text, x, y) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  if (Array.isArray(text)) {
    doc.text(text, x, y);
    return y + text.length * 6;
  } else {
    doc.text(text, x, y);
    return y + 6;
  }
}

function toDMS(deg, isLat) {
  const absolute = Math.abs(deg);
  const degrees = Math.floor(absolute);
  const minutesNotTruncated = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesNotTruncated);
  const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2);
  const direction = isLat ? (deg >= 0 ? "N" : "S") : (deg >= 0 ? "E" : "W");
  return `${degrees}°${minutes}'${seconds}"${direction}`;
}

function getAddressForAirport(codeOrName, airportAddresses) {
  if (!airportAddresses) return undefined;
  let adr = airportAddresses.find(a => a.airport_code === codeOrName);
  if (adr) return adr;
  return airportAddresses.find(
    a =>
      (a.airport_name || "")
        .toLowerCase()
        .replace(/\s+/g, "") ===
      (codeOrName || "")
        .toLowerCase()
        .replace(/\s+/g, "")
  );
}

// Helper function to check if user data is valid for personalization
function hasValidUserDataForPersonalization(user) {
  return user && user.contact_person && user.email;
}

/**
 * Huvudfunktion
 */
export async function exportPenetrationTablesToPDF({
  t,
  showSurfaces,
  setShowSurfaces,
  showEasa,
  setShowEasa,
  showAnnex14,
  setShowAnnex14,
  showEtod,
  setShowEtod,
  showPansOps,
  setShowPansOps,
  showGeoJson,
  setShowGeoJson,
  showUserData,
  setShowUserData,
  showSwedishAirports,
  setShowSwedishAirports,
  showNavaids,
  setShowNavaids,
  showNav,
  setShowNav,
  showTmaTia,
  setShowTmaTia,
  showCtrTizRmz,
  setShowCtrTizRmz,
  setPdfMode,
  analysis,
  omniAnalysis,
  oasAnalysis,
  baroVnavAnalysis,
  vssAnalysis,
  shieldingAnalysis,
  holdingBaseAnalysis,
  holdingRacetrackAnalysis,
  rnavHoldingAnalysis,
  rnpHoldingAnalysis,
  msaAnalysis,
  taaAnalysis,
  circlingAnalysis,
  gnssApchAnalysis,
  locAnalysis,
  features,
  mapDivRef,
  fitMapToFeaturesAndWait,
  airportAddresses,
  userGestureTs = Date.now(),
  user = null
}) {
  // Spara tidigare state
  const prevLayers = {
    showSurfaces,
    showEasa,
    showAnnex14,
    showEtod,
    showPansOps,
    showGeoJson,
    showUserData,
    showSwedishAirports,
    showNavaids,
    showNav,
    showTmaTia,
    showCtrTizRmz
  };

  // Slå av lager inför PDF
  setShowSurfaces(false);
  setShowEasa(false);
  setShowAnnex14(false);
  setShowEtod(false);
  setShowPansOps(false);
  setShowGeoJson(false);
  setShowSwedishAirports(false);
  setShowNavaids(false);
  setShowNav(false);
  setShowTmaTia(false);
  setShowCtrTizRmz(false);
  setShowUserData(true);
  setPdfMode(true);
  try {
  await new Promise(r => setTimeout(r, 200));

  const doc = new jsPDF({ orientation: "portrait" });
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marginaler
  const marginTop = 20;
  const marginBottom = 15;
  const headerHeight = 1;
  const footerHeight = 15;
  const contentAreaTop = marginTop + headerHeight + 10;

  /**
   * Header & Footer
   */
  function addHeader(doc, pageNumber, totalPages, { showTitle = false } = {}) {
    const LOGO_SRC = "/images/flightsafe-analyzer-logo.png";
    const ORIG_W = 500;
    const ORIG_H = 173;
    const MAX_H = 10;
    const MAX_W = 60;
    const TOP_Y = 6;
    const LEFT = 14;
    const DATE_MID_FACTOR = 0.5;
    const TITLE_BASELINE_FACTOR = 0.62;
    const GAP_BELOW_LOGO = 2;
    const PAGE_NUMBER_OFFSET = -2;
    const CONTENT_SPACING = 6;

    const scale = Math.min(MAX_H / ORIG_H, MAX_W / ORIG_W);
    const drawW = Math.round(ORIG_W * scale);
    const drawH = Math.round(ORIG_H * scale);

    try {
      doc.addImage(LOGO_SRC, "PNG", LEFT, TOP_Y, drawW, drawH);
    } catch {
      doc.setDrawColor(150);
      doc.rect(LEFT, TOP_Y, drawW, drawH);
      doc.setFontSize(8);
      doc.text("LOGO", LEFT + 3, TOP_Y + drawH / 2);
    }

    const midline = TOP_Y + drawH * DATE_MID_FACTOR;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const locale = t("language") === "Svenska" ? "sv-SE" : "en-US";
    const dateStr = new Date().toLocaleDateString(locale);
    const dateLabel = `${t("date")}: ${dateStr}`;
    const dateWidth = doc.getTextWidth(dateLabel);
    doc.text(dateLabel, pageWidth - LEFT - dateWidth, midline);

    if (showTitle) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      const titleBaseline = TOP_Y + drawH * TITLE_BASELINE_FACTOR;
      doc.text(t("pdfTitle"), LEFT + drawW + 12, titleBaseline);
    }

    const lineY = TOP_Y + drawH + GAP_BELOW_LOGO;
    doc.setLineWidth(0.5);
    doc.line(LEFT, lineY, pageWidth - LEFT, lineY);

    if (totalPages && pageNumber != null) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const pn = `${pageNumber}/${totalPages}`;
      const pnWidth = doc.getTextWidth(pn);
      const pnY = lineY + PAGE_NUMBER_OFFSET;
      doc.text(pn, pageWidth - LEFT - pnWidth, pnY);
    }

    return lineY + CONTENT_SPACING;
  }

  function addFooter(doc, pageNumber, totalPages) {
    const footerY = pageHeight - footerHeight;
    doc.setLineWidth(0.5);
    doc.line(14, footerY, pageWidth - 14, footerY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      t("pageOf").replace("{current}", pageNumber).replace("{total}", totalPages),
      pageWidth / 2,
      footerY + 7,
      { align: "center" }
    );
    doc.text(t("footerText"), 14, footerY + 7);
  }

  function ensureNotInHeaderOrFooter(doc, y, requiredSpace = 10) {
    const contentAreaBottom = pageHeight - marginBottom - footerHeight;

    if (y < contentAreaTop) y = contentAreaTop;

    if (y + requiredSpace >= contentAreaBottom) {
      doc.addPage();
      y = contentAreaTop;
    }
    return y;
  }

  function addTextWithOffsetLocal(doc, text, x, y, options = {}) {
    y = ensureNotInHeaderOrFooter(doc, y, 10);
    if (Array.isArray(text)) {
      text.forEach(line => {
        y = ensureNotInHeaderOrFooter(doc, y, 6);
        doc.text(line, x, y, options);
        y += 6;
      });
      return y;
    } else {
      // If maxWidth is specified, text will wrap and return array of lines
      const result = doc.text(text, x, y, options);
      // When maxWidth is used, jsPDF returns the text split into lines
      // We need to calculate the y offset based on number of lines
      if (options.maxWidth) {
        const lines = doc.splitTextToSize(text, options.maxWidth);
        y += (lines.length - 1) * 6; // Add spacing for wrapped lines
      }
      return y;
    }
  }

  function addTitleWithOffsetLocal(doc, title, x, y, fontSize = 13) {
    y = ensureNotInHeaderOrFooter(doc, y, 15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(fontSize);
    doc.text(title, x, y);
    return y;
  }

  // Start Y
  let y = contentAreaTop;

  // Huvudrubrik
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  y = addTextWithOffsetLocal(doc, t("exportPdfTitle"), 14, y);
  y += 10;

  // Bakgrund
  y = addTitleWithOffsetLocal(doc, t("background"), 14, y);
  y += 8;

  const today = new Date();
  const datumStr = today.toLocaleDateString(
    t("language") === "Svenska" ? "sv-SE" : "en-US"
  );
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  
  // Build personalized background text with user data
  let backgroundText;
  
  if (hasValidUserDataForPersonalization(user)) {
    // Full user data available - use personalized text
    const contactPerson = user.contact_person;
    const email = user.email;
    const companyName = user.company_name || "";
    
    backgroundText = t("backgroundText1")
      .replace("{date}", datumStr)
      .replace("{contact_person}", contactPerson)
      .replace("{email}", email)
      .replace("{company}", companyName ? `, ${companyName}` : "");
  } else {
    // No user data or incomplete - use fallback translation
    backgroundText = t("backgroundText1Fallback").replace("{date}", datumStr);
  }
  
  y = addTextWithOffsetLocal(
    doc,
    backgroundText,
    14,
    y,
    { maxWidth: pageWidth - 28 } // Enable text wrapping for long personalized text
  );
  y += 6;
  y = addTextWithOffsetLocal(doc, t("backgroundText2"), 14, y);
  y += 8;

  // Berörda flygplatser
  y = addTitleWithOffsetLocal(doc, t("affectedAirports"), 14, y);
  y += 8;

  function normalize(str) {
    return (str || "").toLowerCase().replace(/\s+/g, "");
  }
  let affectedAirports = new Map();

  // Helper: add a single airport (by code+name) to the map if not already present.
  // Deduplication key is always the normalised ICAO code.  When no code is
  // supplied (surface analysis often omits it), we resolve it from
  // airportAddresses by matching the name — this prevents a name-only entry
  // (e.g. "STOCKHOLM/ARLANDA") from appearing alongside the code+name entry
  // (e.g. "ESSA (STOCKHOLM/ARLANDA)") from OMNI/OAS analysis.
  function addAirportToMap(code, name) {
    let normCode = normalize(code);
    const normName = normalize(name);

    // Look up metadata from airportAddresses (one lookup, result reused below)
    let adr;
    if (airportAddresses) {
      if (normCode) {
        adr = airportAddresses.find(a => normalize(a.airport_code) === normCode);
      } else if (normName) {
        adr = airportAddresses.find(a => normalize(a.airport_name) === normName);
        // Resolve the ICAO code via name so the deduplication key is always code-based
        if (adr?.airport_code) {
          normCode = normalize(adr.airport_code);
        }
      }
    }

    const key = normCode || normName;
    if (!key) return;
    if (affectedAirports.has(key)) return;

    const resolvedCode = adr?.airport_code || code || undefined;
    const label =
      resolvedCode && (adr?.airport_name || name)
        ? `${resolvedCode} (${adr?.airport_name || name})`
        : resolvedCode || name;
    affectedAirports.set(key, { code: resolvedCode, name, label });
  }

  if (analysis) {
    analysis.forEach(geoResult => {
      (geoResult.matches || []).forEach(row => {
        addAirportToMap(row.airport_code, row.airport_name);
      });
    });
  }

  features.forEach(f => {
    addAirportToMap(f.properties?.airport_code, f.properties?.airport_name);
  });

  // Collect airports from OMNI analysis only where obstacles exist in results
  if (omniAnalysis && omniAnalysis.results) {
    omniAnalysis.results.forEach(result => {
      if (!result.obstacles || result.obstacles.length === 0) return;
      const code = result.input?.airport_code;
      const name = result.input?.airport_name || code;
      if (name) addAirportToMap(code, name);
    });
  }

  // Collect airports from OAS analysis only where obstacles exist in results
  if (oasAnalysis && oasAnalysis.results) {
    oasAnalysis.results.forEach(result => {
      const hasObstacles = result.categoryResults && result.categoryResults.some(
        c => c.obstacles && c.obstacles.length > 0
      );
      if (!hasObstacles) return;
      const code = result.input?.airport_code;
      const name = result.input?.airport_name || code;
      if (name) addAirportToMap(code, name);
    });
  }

  // Collect airports from BARO/VNAV analysis only where obstacles exist in results
  if (baroVnavAnalysis && baroVnavAnalysis.results) {
    baroVnavAnalysis.results.forEach(result => {
      const hasObstacles = result.categoryResults && result.categoryResults.some(
        c => c.obstacleRows && c.obstacleRows.length > 0
      );
      if (!hasObstacles) return;
      const code = result.input?.airport_code;
      const name = result.input?.airport_name || code;
      if (name) addAirportToMap(code, name);
    });
  }

  // GNSS APCH results are part of the procedure assessment, including errors
  // that must remain visible rather than being reported as no impact.
  for (const result of gnssApchAnalysis?.results || []) {
    if (result.input?.error || (result.segments || []).some(segment => segment.obsRows?.length)) {
      addAirportToMap(result.input?.airport_code, result.input?.airport_name);
    }
  }

  for (const r of locAnalysis?.results || []) {
    if (r.summary.obstaclesAssessed) addAirportToMap(r.input.airport_code, r.input.airport_code);
  }

  let airportsList = Array.from(affectedAirports.values());
  if (airportsList.length === 0)
    airportsList = [{ label: t("noAffectedAirports"), code: "", name: "" }];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  for (const airport of airportsList) {
    y = ensureNotInHeaderOrFooter(doc, y, 12);
    y = addTextWithOffsetLocal(doc, `- ${airport.label}`, 20, y);
    y += 12;
  }

  // Sammanfattning
  const summaryHead = [
    [
      t("object"),
      t("analysis"),
      t("withinSurfaceYes"),
      t("withinSurfaceNo"),
      t("comment"),
      t("summaryCritical"),
    ]
  ];
  const summaryRows = buildSummaryRowsFromAnalysis(analysis, "pdf", t, omniAnalysis, oasAnalysis, baroVnavAnalysis, vssAnalysis, shieldingAnalysis, holdingBaseAnalysis, holdingRacetrackAnalysis, rnavHoldingAnalysis, rnpHoldingAnalysis, msaAnalysis, taaAnalysis, circlingAnalysis, gnssApchAnalysis, locAnalysis);

  // Merge the two indicator columns (utanAnm / anmarkning) into a single "Critical" column.
  // row[6] = "NEJ" means anmarkning (has critical issue) → show t("yes") in red
  // row[5] = "JA" means utanAnm (within surface, no issue) → show t("no") in green
  const summaryRowsForPdf = summaryRows.map(row => {
    const critical = row[6] ? t("yes") : row[5] ? t("no") : "";
    return [row[0], row[1], row[2], row[3], row[4], critical];
  });

  // Adjusted column widths to fill available page width (182mm = 210 - 14 - 14)
  // Total: 14 + 36 + 14 + 14 + 86 + 18 = 182mm
  const columnStyles = {
    0: { halign: "center", cellWidth: 14 },
    1: { halign: "center", cellWidth: 36 },
    2: { halign: "center", cellWidth: 14 },
    3: { halign: "center", cellWidth: 14 },
    4: { cellWidth: 86, overflow: "linebreak", cellPadding: 3, minCellHeight: 8 },
    5: { halign: "center", cellWidth: 18 },
  };

  y = addTitleWithOffsetLocal(doc, t("resultSummary"), 14, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const summaryText = t("resultSummaryText");
  const splitText = doc.splitTextToSize(summaryText, 180);
  y = addTextWithOffsetLocal(doc, splitText, 14, y);
  y += 4;
  // Ensure the summary table always starts at the top of a fresh page (contentAreaTop).
  // In jspdf-autotable 3.5.23, when startY is used mid-page, margin.bottom is not
  // applied on that first page, so rows overflow the footer. Starting at contentAreaTop
  // makes the first page behave identically to subsequent autotable-paginated pages,
  // where margin.bottom is correctly enforced.
  if (y > contentAreaTop) {
    doc.addPage();
    y = contentAreaTop;
  }

  const summaryCommentHooks = createPdfCommentHooks(doc);
  doc.autoTable({
    startY: contentAreaTop,
    head: summaryHead,
    body: summaryRowsForPdf,
    styles: { 
      fontSize: 9, 
      cellPadding: 3,
      minCellHeight: 8,
      overflow: 'linebreak',
      cellWidth: 'wrap'
    },
    headStyles: {
      fillColor: [32, 114, 184],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      minCellHeight: 10
    },
    columnStyles: columnStyles,
    margin: { top: contentAreaTop, bottom: marginBottom + footerHeight + 5, left: 14, right: 14 },
    tableWidth: 182,
    showHead: 'everyPage',
    rowPageBreak: 'auto',
    didParseCell: summaryCommentHooks.didParseCell,
    willDrawCell: function (data) {
      if (data.section === "body" && data.column.index === 2 && (data.cell.raw === "JA" || data.cell.raw === "YES")) {
        data.cell.styles.textColor = [0, 150, 0];
        data.cell.styles.fontStyle = "bold";
      }
      if (data.section === "body" && data.column.index === 3 && (data.cell.raw === "NEJ" || data.cell.raw === "NO")) {
        data.cell.styles.textColor = [200, 0, 0];
        data.cell.styles.fontStyle = "bold";
      }
      if (data.section === "body" && data.column.index === 5) {
        if (data.cell.raw === "Ja" || data.cell.raw === "Yes") {
          data.cell.styles.textColor = [200, 0, 0];
          data.cell.styles.fontStyle = "bold";
        } else if (data.cell.raw === "Nej" || data.cell.raw === "No") {
          data.cell.styles.textColor = [0, 150, 0];
          data.cell.styles.fontStyle = "bold";
        }
      }
      if (data.section === "body" && [2, 3, 5].includes(data.column.index) && !data.cell.raw) {
        data.cell.text = [""];
      }
      summaryCommentHooks.willDrawCell(data);
    },
    didDrawCell: summaryCommentHooks.didDrawCell,
    theme: "grid"
  });
  // Always start the Analysis section on a fresh page so that a long summary
  // table that spans to the previous page cannot overlap with this heading.
  doc.addPage();
  y = contentAreaTop;

  // Analysintroduktion
  y = addTitleWithOffsetLocal(doc, t("analysisTitle"), 14, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  y = addTextWithOffsetLocal(doc, t("analysisIntro"), 14, y);
  y += 6;

  doc.setFontSize(11);
  const analysisParts = [
    t("analysisPart1"),
    t("analysisPart2"),
    t("analysisPart3"),
    t("analysisPart4"),
    t("analysisPart5")
  ];
  for (const part of analysisParts) {
    y = addTextWithOffsetLocal(doc, part, 18, y);
    y += 6;
  }
  y += 10;

  // Lista byggnadsverk
  const buildingsListTitle = t("buildingsList");
  y = addTitleWithOffsetLocal(doc, buildingsListTitle, 14, y);
  y += 10;
  
  // Ensure there's enough space for at least the title and table header together
  const tableRowHeight = 10;
  const tableHeaderHeight = 14;
  const titleAndHeaderSpace = tableHeaderHeight + 20; // Header + minimal spacing
  y = ensureNotInHeaderOrFooter(doc, y, titleAndHeaderSpace);
  
  const pageWidthContent = pageWidth - 28; // Account for margins (14 on each side)
  
  doc.autoTable({
    startY: y,
    head: [[t("objectType"), t("name"), t("height"), t("radius"), t("coordinates")]],
    body: features.map(f => {
      function coordStrDMS(c) {
        if (!Array.isArray(c)) return "";
        if (Array.isArray(c[0])) {
          return c
            .map(pt =>
              Array.isArray(pt)
                ? `(${toDMS(pt[1], true)}, ${toDMS(pt[0], false)}${
                    pt[2] !== undefined ? ", " + pt[2] : ""
                  })`
                : ""
            )
            .join("; ");
        } else {
          return `(${toDMS(c[1], true)}, ${toDMS(c[0], false)}${
            c[2] !== undefined ? ", " + c[2] : ""
          })`;
        }
      }
      let type = f.geometry.type;
      let name = f.properties?.name || "";
      let z = "";
      let radius = "";
      let coords = "";
      if (type === "Point") {
        z = f.geometry.coordinates[2] !== undefined ? f.geometry.coordinates[2] : "";
        coords = coordStrDMS(f.geometry.coordinates);
      } else if (type === "LineString") {
        z =
          f.geometry.coordinates[0][2] !== undefined
            ? f.geometry.coordinates[0][2]
            : "";
        coords = coordStrDMS(f.geometry.coordinates);
      } else if (type === "Polygon") {
        z =
          f.geometry.coordinates[0][0][2] !== undefined
            ? f.geometry.coordinates[0][0][2]
            : "";
        coords = coordStrDMS(f.geometry.coordinates[0]);
      } else if (type === "Circle") {
        z = f.geometry.z !== undefined ? f.geometry.z : "";
        radius = f.geometry.radius !== undefined ? f.geometry.radius : "";
        // The inventory lists the actual centre; perimeter samples belong to
        // the horizontal-tolerance assessment, not the reported coordinates.
        coords = coordStrDMS([f.geometry.center[0], f.geometry.center[1], f.geometry.z]);
      }
      return [type, name, z, radius, coords];
    }),
    styles: { 
      fontSize: 8,
      cellPadding: 3,
      minCellHeight: 10,
      overflow: 'linebreak',
      cellWidth: 'wrap'
    },
    headStyles: { 
      fontSize: 8,
      fillColor: [32, 114, 184],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      minCellHeight: 12
    },
    columnStyles: {
    0: { cellWidth: 'auto', overflow: 'linebreak' },
    1: { cellWidth: 'auto', overflow: 'linebreak' },
    2: { cellWidth: 'auto' },
    3: { cellWidth: 'auto' },
    4: { cellWidth: 'auto', overflow: 'linebreak' }
    },
	
	
    tableWidth: pageWidthContent,
    margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 14, right: 14 },
    showHead: 'everyPage',
    rowPageBreak: 'avoid'
  });
  y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y + 80;

  // Kartbild
  await fitMapToFeaturesAndWait();
  // Capture the Leaflet map itself, excluding sibling UI such as the layer
  // panel and PDF loading overlay. Draw3DMap's pdfMode hides non-obstacle layers.
  const mapDiv = mapDivRef.current?.querySelector('.leaflet-container');
  if (mapDiv) {
    const canvas = await html2canvas(mapDiv, {
      useCORS: true,
      backgroundColor: null,
      // Preserve the map scale and attribution while omitting interactive UI.
      ignoreElements: element => element.matches('.leaflet-control-zoom, .leaflet-draw, .leaflet-pm-toolbar, .leaflet-popup-pane'),
    });
    const imgData = canvas.toDataURL("image/png");
    const imgProps = doc.getImageProperties(imgData);
    const pdfWidth = 180;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    y = ensureNotInHeaderOrFooter(doc, y, pdfHeight + 30);
    y = addTitleWithOffsetLocal(doc, t("mapTitle"), 14, y);
    y += 8;
    y = ensureNotInHeaderOrFooter(doc, y, pdfHeight);
    doc.addImage(imgData, "PNG", 15, y, pdfWidth, pdfHeight);
    y += pdfHeight + 18;
  }

  /**
   * Analyze OCA-related penetrations to generate dynamic explanatory text
   * Returns appropriate text based on which surfaces are penetrated but don't affect OCA
   */
  function analyzeOCAPenetrations(analysis, t) {
    if (!analysis || !Array.isArray(analysis)) return null;

    let lpvPenetrated = false;
    let ilsPenetrated = false;

    // Check all analysis results for LPV OAS and ILS OAS penetrations that don't affect OCA
    analysis.forEach(geoResult => {
      (geoResult.matches || []).forEach(row => {
        // Check if this row represents LPV OAS or ILS OAS surface
        if (row.surface_name && 
            (row.surface_name.includes("LPV OAS") || row.surface_name.includes("ILS OAS"))) {
          
          // Explicitly compare object height (objectZ) against surface height (surfaceZ)
          // Do NOT rely on p.penetrates, instead check if objectZ > surfaceZ
          const actuallyPenetrates = row.penetrations && row.penetrations.some(p => 
            typeof p.z === "number" && typeof p.surface_z === "number" && p.z > p.surface_z
          );
          
          // Check if highlight is false (no OCA-penetration, but surface is penetrated)
          if (actuallyPenetrates && row.highlight === false) {
            if (row.surface_name.includes("LPV OAS")) {
              lpvPenetrated = true;
            }
            if (row.surface_name.includes("ILS OAS")) {
              ilsPenetrated = true;
            }
          }
        }
      });
    });

    // Generate appropriate text based on which surfaces are penetrated
    if (lpvPenetrated && ilsPenetrated) {
      return t("ocaBothSurfaces");
    } else if (lpvPenetrated) {
      return t("ocaLpvOnly");
    } else if (ilsPenetrated) {
      return t("ocaIlsOnly");
    }

    return null; // No OCA-related penetrations found
  }

  /**
   * Helper för penetrationstabeller
   * (UPPDATERAD: Gör rubriker bold, förbättrad cellformatering och sidbrytning)
   */
  function createTableWithOffset(doc, title, regulation, highlight, yRef, t) {
    let rows = [];
    if (!analysis || !Array.isArray(analysis)) return yRef;
    analysis.forEach((geoResult, i) => {
      (geoResult.matches || [])
        .filter(row => {
          return row.regulation === regulation && !!row.highlight === highlight;
        })
        .forEach(row => {
          const objectZs = (row.penetrations || [])
            .map(p => (typeof p.z === "number" ? p.z.toFixed(2) : "-"))
            .join(", ");
          const surfaceZs = (row.penetrations || [])
            .map(p =>
              typeof p.surface_z === "number" ? p.surface_z.toFixed(2) : "-"
            )
            .join(", ");
          const penetrationDiffs = (row.penetrations || [])
            .map(p =>
              typeof p.penetration_delta === "number"
                ? p.penetration_delta.toFixed(2)
                : "-"
            )
            .join(", ");
          rows.push([
            geoResult.properties?.name || t("geometryDefault", { index: i + 1 }),
            row.airport_name || "-",
            row.surface_name || "-",
            row.runway || "-",
            row.regulation || "-",
            objectZs,
            surfaceZs,
            row.highlight ? t("yes") : t("no"),
            penetrationDiffs,
            row.horizontal_tolerance > 0 ? `${row.horizontal_tolerance} m` : "-"
          ]);
        });
    });
    if (rows.length === 0) return yRef;

    const tableRowHeight = 10;
    const tableHeaderHeight = 14;
    
    // Ensure title and table header stay together on same page
    const titleAndHeaderSpace = 8 + tableHeaderHeight + 20;
    yRef = ensureNotInHeaderOrFooter(doc, yRef, titleAndHeaderSpace);
    
    // Bold rubrik
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    yRef = addTextWithOffsetLocal(doc, title, 18, yRef);
    yRef += 4;

    const pageWidthContent = pageWidth - 28; // Account for margins (14 on each side)
    
    doc.autoTable({
      startY: yRef,
      head: [[
        t("object"),
        t("airport"),
        t("surface"),
        t("runway"),
        t("regulation"),
        t("objectHeightM"),
        t("surfaceHeightM"),
        t("penetratesQ"),
        t("penetrationMAll"),
        t("horizontalTolerance") || "Horiz. Tolerance"
      ]],
      body: rows,
      styles: { 
        fontSize: 8,
        cellPadding: 3,
        minCellHeight: 10,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: { 
        fontSize: 8,
        fillColor: [32, 114, 184],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        minCellHeight: 12
      },
     // columnStyles: {
      //  0: { cellWidth: 'auto', overflow: 'linebreak' },
      //  1: { cellWidth: 'auto', overflow: 'linebreak' },
      //  2: { cellWidth: 'auto', overflow: 'linebreak' },
      //  3: { cellWidth: 'auto', overflow: 'linebreak' },
      //  4: { cellWidth: 'auto', overflow: 'linebreak' },
      //  5: { cellWidth: 'auto', overflow: 'linebreak' },
      //  6: { cellWidth: 'auto', overflow: 'linebreak' },
      //  7: { halign: 'center', cellWidth: 'auto' },
      //  8: { halign: 'center', cellWidth: 'auto' },
      //  9: { cellWidth: 'auto', overflow: 'linebreak' }
     // },
	 // Adjusted column widths to fill page width (Total: 182mm for 10 columns)
	 columnStyles: {
  0: { cellWidth: 14 },                       // Objekt
  1: { cellWidth: 38, overflow: 'linebreak' },// Flygplats
  2: { cellWidth: 22 },                       // Yta
  3: { cellWidth: 15 },                       // Bana
  4: { cellWidth: 19 },                       // Regelverk
  5: { cellWidth: 13, halign: 'right' },      // Objekthöjd (m)
  6: { cellWidth: 15, halign: 'right' },      // Ythöjd (m)
  7: { cellWidth: 13, halign: 'center' },     // Påverkan?
  8: { cellWidth: 14, halign: 'center' },     // Antal punkter
  9: { cellWidth: 19, halign: 'right' }       // Penetration (m)
},
	 
      tableWidth: pageWidthContent,
      margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 14, right: 14 },
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
      willDrawCell: function (data) {
        // Mark Impact column cells (index 7) for custom rendering
        if (data.section === "body" && data.column.index === 7) {
          if (data.cell.raw === "Ja" || data.cell.raw === "Yes" || 
              data.cell.raw === "Nej" || data.cell.raw === "No") {
            data.cell._renderImpact = true;
            data.cell._impactValue = data.cell.raw;
            // Clear default text to prevent auto-rendering
            data.cell.text = [];
          }
        }
      },
      didDrawCell: function (data) {
        // Custom render Impact values in column 7 with color
        if (data.section === "body" && data.cell._renderImpact) {
          const x = data.cell.x + (data.cell.width / 2); // Center horizontally
          const y = data.cell.y + data.cell.padding('top') + (data.cell.styles.fontSize * 0.7); // Adjust for baseline
          
          doc.setFontSize(data.cell.styles.fontSize);
          doc.setFont(data.cell.styles.font || 'helvetica', 'bold');
          
          // Ja/Yes (impact) = red, Nej/No (no impact) = green
          if (data.cell._impactValue === "Ja" || data.cell._impactValue === "Yes") {
            doc.setTextColor(200, 0, 0); // Red
          } else if (data.cell._impactValue === "Nej" || data.cell._impactValue === "No") {
            doc.setTextColor(0, 150, 0); // Green
          }
          
          doc.text(data.cell._impactValue, x, y, { align: 'center' });
          
          // Reset to defaults
          doc.setFont(data.cell.styles.font || 'helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
        }
      }
    });
    return doc.lastAutoTable ? doc.lastAutoTable.finalY + 7 : yRef + 50;
  }

  // Section 2 main heading
  y = addTitleWithOffsetLocal(doc, t("impactOnSurfaces"), 14, y);
  y += 10;

  // ANNEX 14
  const annex14PenRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "ANNEX 14" && !!row.highlight === true
          )
        )
      : [];
  const annex14InomRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "ANNEX 14" && !!row.highlight === false
          )
        )
      : [];

  y = addTitleWithOffsetLocal(doc, t("impactOnSurfacesAnnex14"), 14, y);
  y += 10;
  if (annex14PenRows.length > 0 || annex14InomRows.length > 0) {
    if (annex14PenRows.length > 0)
      y = createTableWithOffset(doc, t("penetratesAnnex14"), "ANNEX 14", true, y, t);
    if (annex14InomRows.length > 0)
      y = createTableWithOffset(doc, t("noPenetrationAnnex14"), "ANNEX 14", false, y, t);
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addTextWithOffsetLocal(doc, t("noImpact"), 18, y);
    y += 10;
  }

  // eTOD
  const etodPenRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "etod" && !!row.highlight === true
          )
        )
      : [];
  const etodInomRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "etod" && !!row.highlight === false
          )
        )
      : [];
  y = addTitleWithOffsetLocal(doc, t("impactOnSurfacesEtod"), 14, y);
  y += 10;
  if (etodPenRows.length > 0 || etodInomRows.length > 0) {
    if (etodPenRows.length > 0) {
      y = createTableWithOffset(doc, t("penetratesEtod"), "etod", true, y, t);
      // KLARIFIERING EFTER PENETRATION (eTOD) - formatted with better spacing
      y += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(70, 70, 70); // Slightly gray for clarification text
      const clarLines = doc.splitTextToSize(t("etodClarification"), 175);
      y = ensureNotInHeaderOrFooter(doc, y, clarLines.length * 6 + 10);
      clarLines.forEach(line => {
        y = addTextWithOffsetLocal(doc, line, 20, y);
        y += 5;
      });
      doc.setTextColor(0, 0, 0); // Reset to black
      y += 8;
    }
    if (etodInomRows.length > 0) {
      y = createTableWithOffset(doc, t("noPenetrationEtod"), "etod", false, y, t);
    }
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addTextWithOffsetLocal(doc, t("noImpact"), 18, y);
    y += 10;
  }

  // Section 3: Impact on airports' airspace
  y = addTitleWithOffsetLocal(doc, t("impactOnAirspace"), 14, y);
  y += 10;
  const airspaceMatches = analysis && Array.isArray(analysis)
    ? analysis.flatMap(geoResult =>
        (geoResult.matches || []).filter(
          row => typeof row.airspace_type === "string" && row.airspace_type.trim() !== ""
        )
      )
    : [];
  if (airspaceMatches.length > 0) {
    const uniqueAirspaces = [...new Set(airspaceMatches.map(m => [m.airspace_name, m.airspace_type].filter(Boolean).join(" ")))];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const warningText = t("airspaceWarning")
      .replace("{count}", airspaceMatches.length)
      .replace("{airspaces}", uniqueAirspaces.join(", "));
    const splitWarning = doc.splitTextToSize(warningText, 180);
    for (const line of splitWarning) {
      y = addTextWithOffsetLocal(doc, line, 18, y);
      y += 5;
    }
    y += 5;
    const splitWarningText = doc.splitTextToSize(t("airspaceWarningText"), 180);
    for (const line of splitWarningText) {
      y = addTextWithOffsetLocal(doc, line, 18, y);
      y += 5;
    }
    y += 5;
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addTextWithOffsetLocal(doc, t("noAirspaceText"), 18, y);
    y += 10;
  }

  // EASA (ATS vector altitude) - part of Section 3: Impact on airports' airspace
  const easaPenRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "EASA" && !!row.highlight === true
          )
        )
      : [];
  const easaInomRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "EASA" && !!row.highlight === false
          )
        )
      : [];
  y = addTitleWithOffsetLocal(doc, t("impactOnSurfacesEasa"), 14, y);
  y += 10;
  if (easaPenRows.length > 0 || easaInomRows.length > 0) {
    if (easaPenRows.length > 0)
      y = createTableWithOffset(doc, t("penetratesEasa"), "EASA", true, y, t);
    if (easaInomRows.length > 0)
      y = createTableWithOffset(doc, t("noPenetrationEasa"), "EASA", false, y, t);
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addTextWithOffsetLocal(doc, t("noImpact"), 18, y);
    y += 10;
  }

  // PANS OPS
  const pansOpsPenRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "PANS OPS" && !!row.highlight === true
          )
        )
      : [];
  const pansOpsInomRows =
    analysis && Array.isArray(analysis)
      ? analysis.flatMap(geoResult =>
          (geoResult.matches || []).filter(
            row => row.regulation === "PANS OPS" && !!row.highlight === false
          )
        )
      : [];
  y = addTitleWithOffsetLocal(doc, t("impactOnProcedures"), 14, y);
  y += 10;
  
  // Check if there are OMNI obstacles (OMNI is part of procedures)
  const hasOmniObstacles = omniAnalysis && omniAnalysis.results && omniAnalysis.results.length > 0 
    && omniAnalysis.results.some(result => result.obstacles && result.obstacles.length > 0);
  const hasGnssResults = gnssApchAnalysis?.results?.length > 0;
  const hasLocResults = locAnalysis?.results?.some(r => r.summary.obstaclesAssessed > 0);
  
  if (pansOpsPenRows.length > 0 || pansOpsInomRows.length > 0) {
    if (pansOpsPenRows.length > 0)
      y = createTableWithOffset(doc, t("penetratesPansOps"), "PANS OPS", true, y, t);
    if (pansOpsInomRows.length > 0)
      y = createTableWithOffset(doc, t("noPenetrationPansOps"), "PANS OPS", false, y, t);
  } else if (!hasOmniObstacles && !hasGnssResults && !hasLocResults) {
    // GNSS assessment status is reported per procedure/category below.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addTextWithOffsetLocal(doc, t("noImpact"), 18, y);
    y += 10;
  }

  // OCA EXPLANATORY TEXT - Dynamic text based on which surfaces are penetrated
  const ocaText = analyzeOCAPenetrations(analysis, t);
  if (ocaText) {
    y = ensureNotInHeaderOrFooter(doc, y, 25);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70); // Slightly gray for explanatory text
    const ocaLines = doc.splitTextToSize(ocaText, 175);
    ocaLines.forEach(line => {
      y = addTextWithOffsetLocal(doc, line, 20, y);
      y += 5;
    });
    doc.setTextColor(0, 0, 0); // Reset to black
    y += 10;
  }

  // OMNI Departures Section - Add OMNI table if OMNI analysis exists
  if (omniAnalysis && omniAnalysis.results && omniAnalysis.results.length > 0) {
    // Check if there are any obstacles
    const hasObstacles = omniAnalysis.results.some(result => 
      result.obstacles && result.obstacles.length > 0
    );
    
    if (hasObstacles) {
      // Add subheading "OMNI Departures"
      y = ensureNotInHeaderOrFooter(doc, y, 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, t("omniDepartures"), 18, y);
      y += 8;

      const omniTableHead = [[
        t("airport") || "Airport",
        t("runway") || "Runway",
        t("name"),
        t("pdfOmniArea"),
        t("pdfOmniElevM"),
        t("pdfOmniDrDist"),
        t("pdfOmniDoDist"),
        t("pdfOmniReqMoc"),
        t("pdfOmniActAlt"),
        t("pdfOmniReqAlt"),
        t("pdfOmniPdg"),
        t("pdfOmniCritical"),
        t("pdfOmniCio")
      ]];

      const omniTableBody = [];
      let hasCIO = false;

      for (const result of omniAnalysis.results) {
        if (!result.obstacles || result.obstacles.length === 0) continue;

        // Per-result heading + input params table
        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const omniResultHeading = `${result.input.airport_code || '—'} – ${result.input.airport_name || ''} – RWY ${result.input.runway_designation || '—'}`;
        y = addTextWithOffsetLocal(doc, omniResultHeading.replace(/ – $/, ''), 18, y);
        y += 4;

        const rwyStartElevStr = result.input.runway_start_elev_m != null
          ? `${parseFloat(result.input.runway_start_elev_m).toFixed(1)} m / ${result.input.runway_start_elev_ft} ft` : '—';
        const derElevStr = result.input.der_position_elev_m != null
          ? `${parseFloat(result.input.der_position_elev_m).toFixed(1)} m / ${result.input.der_position_elev_ft} ft` : '—';
        const turnAltStr = result.input.turning_altitude_ft != null
          ? `${result.input.turning_altitude_ft} ft / ${result.input.turning_altitude_m != null ? parseFloat(result.input.turning_altitude_m).toFixed(1) : '—'} m` : '—';
        const nextSegStr = result.input.next_segment_altitude_ft != null
          ? `${result.input.next_segment_altitude_ft} ft / ${result.input.next_segment_altitude_m != null ? parseFloat(result.input.next_segment_altitude_m).toFixed(1) : '—'} m` : '—';
        const minTurnHStr = result.input.min_turn_height_m != null ? `${result.input.min_turn_height_m} m` : '—';
        const minMocStr = result.input.min_moc_m != null ? `${result.input.min_moc_m} m` : '—';
        const mocPctStr = result.input.moc_percentage != null ? `${result.input.moc_percentage}%` : '—';
        const pdgPctStr = result.input.pdg_percentage != null ? `${result.input.pdg_percentage}%` : '—';
        const catHStr = result.input.category_h ? (t('yes') || 'Yes') : (t('no') || 'No');
        const allowTurnsStr = result.input.allow_turns_before_der ? (t('yes') || 'Yes') : (t('no') || 'No');

        const omniInputBody = [
          ['Runway Start', `${result.input.runway_start_lat || '—'} / ${result.input.runway_start_lon || '—'}`, 'Runway Start Elev', rwyStartElevStr, '', ''],
          ['DER Position', `${result.input.der_position_lat || '—'} / ${result.input.der_position_lon || '—'}`, 'DER Elev', derElevStr, '', ''],
          ['Turning Altitude', turnAltStr, 'Next Segment Alt', nextSegStr, 'Min Turn Height', minTurnHStr],
          ['Min MOC', minMocStr, 'MOC %', mocPctStr, 'PDG %', pdgPctStr],
          ['Category H', catHStr, 'Allow Turns Before DER', allowTurnsStr, '', ''],
        ];

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.autoTable({
          startY: Math.max(y, contentAreaTop),
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: omniInputBody,
          styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 32, fontStyle: 'bold' },
            1: { cellWidth: 44 },
            2: { cellWidth: 30, fontStyle: 'bold' },
            3: { cellWidth: 36 },
            4: { cellWidth: 24, fontStyle: 'bold' },
            5: { cellWidth: 'auto' },
          },
          margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
          tableWidth: 'auto',
          showHead: 'firstPage',
          theme: 'grid',
        });
        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 4 : y + 20;

        const airportCode = result.input.airport_code || "-";
        const runway = result.input.runway_designation || "-";

        for (const obstacle of result.obstacles) {
          if (obstacle.isCloseIn) {
            hasCIO = true;
          }

          const obstacleName = obstacle.obstacleName || "-";
          const area = obstacle.areaType || "-";
          const elevation = obstacle.obstacleElevation != null
            ? obstacle.obstacleElevation.toFixed(1)
            : "-";
          const drDist = obstacle.distanceDr != null
            ? obstacle.distanceDr.toFixed(0)
            : "-";
          const doDist = obstacle.distanceDo != null
            ? obstacle.distanceDo.toFixed(0)
            : "-";
          const reqMoc = obstacle.requiredMocFt != null
            ? obstacle.requiredMocFt.toFixed(0)
            : "-";
          const actAlt = obstacle.actualAltitudeFt != null
            ? obstacle.actualAltitudeFt.toFixed(0)
            : "-";
          const reqAlt = obstacle.requiredAltitudeFt != null
            ? obstacle.requiredAltitudeFt.toFixed(0)
            : "-";
          const pdg = obstacle.pdgRequired != null
            ? obstacle.pdgRequired.toFixed(2) + "%"
            : "-";
          const critical = obstacle.isCritical ? (t("language") === "Svenska" ? "Ja" : "Yes") : "";
          const closeIn = obstacle.isCloseIn ? (t("language") === "Svenska" ? "Ja" : "Yes") : "";

          omniTableBody.push([
            airportCode,
            runway,
            obstacleName,
            area,
            elevation,
            drDist,
            doDist,
            reqMoc,
            actAlt,
            reqAlt,
            pdg,
            critical,
            closeIn
          ]);
        }
      }

      // Add OMNI obstacle table
      if (omniTableBody.length > 0) {
        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, t('pdfOmniObstacleTable'), 18, y);
        y += 4;

        doc.autoTable({
          startY: Math.max(y, contentAreaTop),
          head: omniTableHead,
          body: omniTableBody,
          styles: {
            fontSize: 7,
            cellPadding: 2,
            minCellHeight: 6,
            overflow: 'linebreak'
          },
          headStyles: {
            fillColor: [32, 114, 184],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            minCellHeight: 8,
            fontSize: 7
          },
          columnStyles: {
            0: { cellWidth: 16, halign: "center" },
            1: { cellWidth: 11, halign: "center" },
            2: { cellWidth: 20, halign: "left" },
            3: { cellWidth: 11, halign: "center" },
            4: { cellWidth: 11, halign: "right" },
            5: { cellWidth: 11, halign: "right" },
            6: { cellWidth: 11, halign: "right" },
            7: { cellWidth: 11, halign: "right" },
            8: { cellWidth: 11, halign: "right" },
            9: { cellWidth: 11, halign: "right" },
            10: { cellWidth: 13, halign: "right" },
            11: { cellWidth: 11, halign: "center" },
            12: { cellWidth: 11, halign: "center" }
          },
          margin: { top: contentAreaTop, bottom: marginBottom + footerHeight },
          tableWidth: 'wrap',
          showHead: 'everyPage',
          rowPageBreak: 'avoid',
          didParseCell: (data) => {
            if (data.section === 'body') {
              const critical = omniTableBody[data.row.index]?.[11];
              if (critical === 'Yes' || critical === 'Ja') {
                data.cell.styles.textColor = [183, 28, 28];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          theme: "grid"
        });
        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
      }

      // Add CIO note if applicable
      if (hasCIO) {
        y = ensureNotInHeaderOrFooter(doc, y, 15);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        const cioNoteText = t("cioNote");
        const splitCioText = doc.splitTextToSize(cioNoteText, 175);
        y = addTextWithOffsetLocal(doc, splitCioText, 18, y);
        y += 10;
      }
    }
  }

  // OAS Calculation Section - placed right after OMNI
  if (oasAnalysis && oasAnalysis.results && oasAnalysis.results.length > 0) {
    const oasResultsWithObstacles = oasAnalysis.results.filter(r =>
      r.categoryResults && r.categoryResults.some(c => c.obstacles && c.obstacles.length > 0)
    );

    if (oasResultsWithObstacles.length > 0) {
      // Subtitle: OAS
      y = ensureNotInHeaderOrFooter(doc, y, 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, t("oasResults") || "OAS Calculation Results", 18, y);
      y += 8;

      for (const result of oasResultsWithObstacles) {
        // Per-result heading
        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const oasResultHeading = `${result.input.airport_code} – ${result.input.airport_name || ''} – RWY ${result.input.runway_designation} (${result.input.oas_category})`;
        y = addTextWithOffsetLocal(doc, oasResultHeading.replace(/ – $/, ''), 18, y);
        y += 4;

        // Input Parameters table for this result
        {
          const rdhStr = result.input.rdh_ft != null
            ? `${(result.input.rdh_ft * 0.3048).toFixed(1)} m / ${result.input.rdh_ft} ft` : '—';
          const dltStr = result.input.dlt_m != null ? `${result.input.dlt_m} m` : '—';
          const macgStr = result.input.macg_pct != null ? `${result.input.macg_pct}%` : '—';
          const maMethodStr = result.input.ma_evaluation_method === 'STANDARD' ? 'Xz standard'
            : result.input.ma_evaluation_method === 'ALTERNATIVE' ? "VPA'"
            : result.input.ma_evaluation_method || '—';
          const thrPosStr = result.input.thr_lat != null
            ? `${toDMS(result.input.thr_lat, true)} / ${toDMS(result.input.thr_lon, false)}` : '—';
          const thrElevStr = result.input.thr_elev_m != null
            ? `${result.input.thr_elev_m.toFixed(1)} m` : '—';
          const inboundStr = result.input.inbound_track_deg != null
            ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM: ${result.input.utm_grid_bearing_deg != null ? result.input.utm_grid_bearing_deg.toFixed(2) : '—'}°)`
            : '—';
          const oasInputBody = [
            ['GPA (°)', result.input.gpa_deg != null ? `${result.input.gpa_deg}°` : '—', 'RDH', rdhStr, 'DLT', dltStr],
            ['MA Climb Gradient', macgStr, 'MA Eval. Method', maMethodStr, 'OAS Category', result.input.oas_category || '—'],
            ['THR Position', thrPosStr, 'THR Elevation', thrElevStr, 'Inbound Track', inboundStr],
          ];
          if (result.input.paoas_designator) {
            const mrvaStr = result.input.mrva_ft != null ? `${result.input.mrva_ft} ft` : '—';
            oasInputBody.push(['PAOAS Designator', result.input.paoas_designator, 'MRVA', mrvaStr, '', '']);
          }
          if (result.input.preceding_segment_alt_ft != null) {
            const mocStr = result.input.preceding_segment_moc_m != null ? `${result.input.preceding_segment_moc_m} m` : '—';
            oasInputBody.push(['Interm. Segment Alt', `${result.input.preceding_segment_alt_ft} ft`, 'Interm. Segment MOC', mocStr, '', '']);
          }

          y = ensureNotInHeaderOrFooter(doc, y, 10);
          doc.autoTable({
            startY: Math.max(y, contentAreaTop),
            head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
            body: oasInputBody,
            styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
            headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 32, fontStyle: 'bold' },
              1: { cellWidth: 40 },
              2: { cellWidth: 28, fontStyle: 'bold' },
              3: { cellWidth: 36 },
              4: { cellWidth: 24, fontStyle: 'bold' },
              5: { cellWidth: 'auto' },
            },
            margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
            tableWidth: 'auto',
            showHead: 'firstPage',
            theme: 'grid',
          });
          y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
        }

        for (const catRes of result.categoryResults) {
          if (!catRes.obstacles || catRes.obstacles.length === 0) continue;

          // Per runway/category heading
          y = ensureNotInHeaderOrFooter(doc, y, 16);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          const rwyHeading = `${result.input.airport_code} – RWY ${result.input.runway_designation} (${result.input.oas_category}) – Cat ${catRes.acCategory}`;
          y = addTextWithOffsetLocal(doc, rwyHeading, 18, y);
          y += 4;

          // OCA line
          if (catRes.obstacleOcaM != null) {
            y = ensureNotInHeaderOrFooter(doc, y, 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            const criticalCount = catRes.criticalObstacles || 0;
            const criticalLabel = t('critical') || 'Critical';
            const ocaPrefix = `OCA = ${catRes.obstacleOcaM} m / ${catRes.obstacleOcaFt} ft  |  ${t('totalObstaclesAssessed') || 'Total Obstacles Assessed'}: ${catRes.obstaclesChecked || 0}  |  `;
            const criticalPart = `${criticalLabel}: ${criticalCount}`;
            if (criticalCount > 0) {
              doc.text(ocaPrefix, 18, y);
              const prefixWidth = doc.getTextWidth(ocaPrefix);
              doc.setFont("helvetica", "bold");
              doc.setTextColor(200, 0, 0);
              doc.text(criticalPart, 18 + prefixWidth, y);
              doc.setTextColor(0, 0, 0);
              doc.setFont("helvetica", "normal");
            } else {
              doc.text(`${ocaPrefix}${criticalPart}`, 18, y);
            }
            y += 10; // same total advancement as the replaced addTextWithOffsetLocal (6px line) + y+=4 spacing
          }

          // Build OAS obstacle table
          const oasHead = [[
            t('airport') || 'Airport',
            t('runway') || 'Runway',
            t('oasObstacleName') || 'Obstacle',
            t('oasSurface') || 'Surface',
            'ILS X (m)',
            'ILS Y (m)',
            t('oasSurfaceAltM') || 'Surf. Alt (m)',
            'Diff (m)',
            t('oasOcaM') || 'OCA (m)',
            t('oasOcaFt') || 'OCA (ft)',
            t('oasCritical') || 'Critical',
          ]];

          const oasBody = catRes.obstacles.map(obs => [
            result.input.airport_code || '-',
            result.input.runway_designation || '-',
            obs.obstacleName || '-',
            obs.surface || '-',
            obs.ilsX != null ? obs.ilsX.toFixed(1) : '-',
            obs.ilsY != null ? obs.ilsY.toFixed(1) : '-',
            obs.surfaceAltM != null ? obs.surfaceAltM.toFixed(1) : '-',
            obs.differenceM != null ? obs.differenceM.toFixed(2) : '-',
            obs.ocaM != null ? String(obs.ocaM) : '-',
            obs.ocaFt != null ? String(obs.ocaFt) : '-',
            obs.isCritical ? (t('language') === 'Svenska' ? 'Ja' : 'Yes') : '',
          ]);

          doc.autoTable({
            startY: Math.max(y, contentAreaTop),
            head: oasHead,
            body: oasBody,
            styles: { fontSize: 7, cellPadding: 2, minCellHeight: 6, overflow: 'linebreak' },
            headStyles: {
              fillColor: [32, 114, 184],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              halign: 'center',
              fontSize: 7,
            },
            columnStyles: {
              0: { cellWidth: 14, halign: 'center' },
              1: { cellWidth: 10, halign: 'center' },
              2: { cellWidth: 22, halign: 'left' },
              3: { cellWidth: 10, halign: 'center' },
              4: { cellWidth: 14, halign: 'right' },
              5: { cellWidth: 14, halign: 'right' },
              6: { cellWidth: 16, halign: 'right' },
              7: { cellWidth: 14, halign: 'right' },
              8: { cellWidth: 14, halign: 'right' },
              9: { cellWidth: 14, halign: 'right' },
              10: { cellWidth: 14, halign: 'center' },
            },
            margin: { top: contentAreaTop, bottom: marginBottom + footerHeight },
            tableWidth: 'wrap',
            showHead: 'everyPage',
            rowPageBreak: 'avoid',
            didParseCell: (data) => {
              if (data.section === 'body') {
                const critical = oasBody[data.row.index]?.[10];
                if (critical === 'Yes' || critical === 'Ja') {
                  data.cell.styles.textColor = [183, 28, 28];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            },
            theme: 'grid',
          });
          y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
        }
      }
    }
  }

  // BARO/VNAV OAS Section - placed right after OAS
  if (baroVnavAnalysis && baroVnavAnalysis.results && baroVnavAnalysis.results.length > 0) {
    const baroResultsWithCats = baroVnavAnalysis.results.filter(r =>
      r.categoryResults && r.categoryResults.some(c => !c.error && c.obstacleRows && c.obstacleRows.length > 0)
    );

    if (baroResultsWithCats.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, t("baroVnavResults") || "BARO/VNAV OAS Calculation Results", 18, y);
      y += 8;

      for (const result of baroResultsWithCats) {
        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const baroRwyHeading = `${result.input.airport_code} – RWY ${result.input.runway_designation}`;
        y = addTextWithOffsetLocal(doc, baroRwyHeading, 18, y);
        y += 4;

        // Input Parameters table (matches HTML layout)
        {
          const rdhStr = result.input.rdh_ft != null
            ? `${(result.input.rdh_ft * 0.3048).toFixed(1)} m / ${result.input.rdh_ft} ft` : '—';
          const arpStr = result.input.arp_elev_m != null && result.input.arp_elev_ft != null
            ? `${result.input.arp_elev_m} m / ${result.input.arp_elev_ft} ft` : '—';
          const thrElevStr = result.input.thr_elev_m != null
            ? `${result.input.thr_elev_m.toFixed(1)} m / ${Math.round(result.input.thr_elev_m / 0.3048)} ft` : '—';
          const evalMethodLabel = result.input.eval_method_ma === 'STANDARD' ? 'Xz standard'
            : result.input.eval_method_ma === 'ALTERNATIVE' ? "VPA'" : result.input.eval_method_ma || '—';
          const thrPosStr = result.input.thr_lat != null
            ? `${toDMS(result.input.thr_lat, true)} / ${toDMS(result.input.thr_lon, false)}` : '—';
          const inboundStr = result.input.inbound_track_deg != null
            ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM: ${result.input.utm_grid_bearing_deg != null ? result.input.utm_grid_bearing_deg.toFixed(2) : '—'}°)`
            : '—';

          const fafDistanceNm = result.input.effective_thr_faf_distance_nm ?? result.input.thr_faf_distance_nm;
          const fafDistanceSource = result.input.thr_faf_distance_source === 'calculated'
            ? (t('language') === 'Svenska' ? 'Beräknat' : 'Calculated')
            : (t('language') === 'Svenska' ? 'Angivet' : 'Entered');
          const fafDistanceStr = fafDistanceNm != null
            ? `${Number(fafDistanceNm).toFixed(3)} NM\n${fafDistanceSource}` : '—';

          const baroInputBody = [
            [
              t('baroVnavVpa') || 'VPA (°)',
              result.input.vertical_path_angle_deg != null ? `${result.input.vertical_path_angle_deg}°` : '—',
              t('baroVnavRdh') || 'RDH', rdhStr,
              t('baroVnavMinAlt') || 'Min Altitude (ft)',
              result.input.min_altitude_ft != null ? `${result.input.min_altitude_ft} ft` : '—',
            ],
            [
              t('baroVnavMinTemp') || 'Min Temperature (°C)',
              result.input.minimum_temperature_c != null ? `${result.input.minimum_temperature_c}°C` : '—',
              t('baroVnavIsaDev') || 'ISA Deviation (°C)',
              result.input.isa_deviation_c != null ? `${result.input.isa_deviation_c}°C` : '—',
              t('baroVnavMacg') || 'MACG (%)',
              result.input.macg_percent != null ? `${result.input.macg_percent}%` : '—',
            ],
            [
              t('baroVnavEvalMethod') || 'MA Evaluation Method', evalMethodLabel,
              t('baroVnavArpElevFt') || 'ARP Elev', arpStr,
              'THR Elev', thrElevStr,
            ],
            [
              'THR Position', thrPosStr,
              t('baroVnavInboundTrack') || 'Inbound Track (°)', inboundStr,
              'THR - FAF/FAP', fafDistanceStr,
            ],
          ];
          if (result.input.offset_final_approach_track) {
            baroInputBody.push([
              t('baroVnavOffsetAngle') || 'Offset Angle (°)',
              result.input.offset_angle_deg != null ? `${result.input.offset_angle_deg}°` : '—',
              '', '', '', '',
            ]);
          }

          y = ensureNotInHeaderOrFooter(doc, y, 10);
          doc.autoTable({
            startY: Math.max(y, contentAreaTop),
            head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
            body: baroInputBody,
            styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
            headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 30, fontStyle: 'bold' },
              1: { cellWidth: 34 },
              2: { cellWidth: 26, fontStyle: 'bold' },
              3: { cellWidth: 32 },
              4: { cellWidth: 24, fontStyle: 'bold' },
              5: { cellWidth: 28 },
            },
            margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
            tableWidth: 'auto',
            showHead: 'firstPage',
            theme: 'grid',
          });
          y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
        }

        // Build BARO/VNAV parameter table
        const hasOffset = result.input.offset_final_approach_track;
        const baroHead = [
          [
            t('pdfBaroAircraftCategory'),
            t('baroVnavIas') || 'Max IAS (kts)',
            t('pdfBaroIasAtThr'),
            t('baroVnavOch') || 'OCH (m)',
            t('pdfBaroHeightLoss'),
            t('pdfBaroHlCorrection'),
            t('pdfBaroTempCorr'),
            t('baroVnavXfap') || 'X FAP (m)',
            t('baroVnavXfas') || 'X FAS',
            t('pdfBaroFasAngle'),
            t('baroVnavXz') || 'Xz',
            t('baroVnavMinVpa') || 'Min VPA (°)',
            t('pdfBaroMaxTemp35'),
            ...(hasOffset ? [
              t('baroVnavDMin') || 'Min D (m)',
              t('baroVnavOchMin') || 'Min OCH (m)',
              t('baroVnavOchMinFt') || 'Min OCH (ft)',
              t('baroVnavOcaMin') || 'Min OCA (m)',
              t('baroVnavOcaMinFt') || 'Min OCA (ft)',
            ] : []),
          ]
        ];

        const baroBody = result.categoryResults
          .filter(c => !c.error)
          .map(cat => [
            cat.acCategory,
            cat.iasKts,
            cat.iasAtThrKts,
            cat.ochM != null ? cat.ochM.toFixed(1) : '—',
            cat.hlM != null ? cat.hlM : '—',
            cat.hlCorrectionM != null ? cat.hlCorrectionM : '—',
            cat.tcM != null ? cat.tcM.toFixed(1) : '—',
            cat.xfapM != null ? cat.xfapM : '—',
            cat.xfasM != null ? cat.xfasM.toFixed(2) : '—',
            cat.fasAngleDeg != null ? cat.fasAngleDeg.toFixed(2) : '—',
            cat.xzM != null ? cat.xzM.toFixed(0) : '—',
            cat.minVPADeg != null ? cat.minVPADeg.toFixed(2) : '—',
            cat.maxTempAt35VPA != null ? cat.maxTempAt35VPA.toFixed(1) : '—',
            ...(hasOffset ? [
              cat.offsetCalc ? cat.offsetCalc.dMinM.toFixed(1) : '—',
              cat.offsetCalc ? cat.offsetCalc.ochMinM.toFixed(1) : '—',
              cat.offsetCalc ? cat.offsetCalc.ochMinFt.toFixed(1) : '—',
              cat.offsetCalc ? cat.offsetCalc.ocaMinM.toFixed(1) : '—',
              cat.offsetCalc ? cat.offsetCalc.ocaMinFt.toFixed(1) : '—',
            ] : []),
          ]);

        if (baroBody.length > 0) {
          y = ensureNotInHeaderOrFooter(doc, y, 10);
          doc.autoTable({
            head: baroHead,
            body: baroBody,
            startY: Math.max(y, contentAreaTop),
            margin: { left: 18, right: 18, top: contentAreaTop, bottom: marginBottom + footerHeight },
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
            showHead: 'everyPage',
            rowPageBreak: 'avoid',
            theme: 'grid',
          });
          y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
        }

        // Obstacle evaluation tables – one per category (matching exact columns requested)
        for (const cat of result.categoryResults.filter(c => !c.error)) {
          y = ensureNotInHeaderOrFooter(doc, y, 14);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          // Three cases:
          //   publishedOcaFt != null  → row found + value present → show it
          //   minimaFound but null    → row found but category column is NULL → say nothing (all obstacles critical by rule)
          //   !minimaFound            → no row at all → note "no published minima"
          const publishedStr = cat.publishedOcaFt != null
            ? `  |  ${t('baroVnavPublishedMinima') || 'Published'}: ${cat.publishedOcaFt} ft`
            : (!cat.minimaFound ? `  |  (${t('baroVnavNoPublishedMinima') || 'no published minima'})` : '');
          const obsHeading = `${t('baroVnavAircraftCategory') || 'Aircraft Category'}: ${cat.acCategory}${cat.maxOcaM != null && cat.maxOcaFt != null ? `  |  OCA: ${cat.maxOcaM} m / ${cat.maxOcaFt} ft` : ''}${publishedStr}`;
          y = addTextWithOffsetLocal(doc, obsHeading, 18, y);
          y += 3;

          if (!cat.obstacleRows || cat.obstacleRows.length === 0) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(8);
            y = addTextWithOffsetLocal(doc, t('baroVnavNoObstacles') || 'No obstacles within protection surfaces', 22, y);
            y += 4;
            continue;
          }

          const obsHead = [[
            t('baroVnavObstName') || 'Obstacle Name',
            t('baroVnavObstAlt') || 'Obstacle Alt (m)',
            t('baroVnavArea') || 'Area',
            t('baroVnavMocM') || 'MOC (m)',
            t('baroVnavMocFt') || 'MOC (ft)',
            t('baroVnavEqHeightM') || 'Equiv. Height (m)',
            t('baroVnavEqHeightFt') || 'Equiv. Height (ft)',
            t('baroVnavEqAltM') || 'Equiv. Alt (m)',
            t('baroVnavEqAltFt') || 'Equiv. Alt (ft)',
            t('baroVnavSurface') || 'Surface',
            t('baroVnavSurfAltM') || 'Surface Alt (m)',
            t('baroVnavSurfAltFt') || 'Surface Alt (ft)',
            t('baroVnavDiffM') || 'Diff (m)',
            t('baroVnavDiffFt') || 'Diff (ft)',
            t('baroVnavHlM') || 'HL (m)',
            t('baroVnavHlFt') || 'HL (ft)',
            t('baroVnavOcaM') || 'OCA (m)',
            t('baroVnavOcaFt') || 'OCA (ft)',
            t('baroVnavCritical') || 'Critical',
          ]];

          const obsBody = cat.obstacleRows.map(row => [
            row.obstacleName,
            row.obstacleAltM != null ? row.obstacleAltM : '—',
            row.area,
            row.mocM != null ? row.mocM : '—',
            row.mocFt != null ? row.mocFt : '—',
            row.eqHeightM != null ? row.eqHeightM : '—',
            row.eqHeightFt != null ? row.eqHeightFt : '—',
            row.eqAltM  != null ? row.eqAltM  : '—',
            row.eqAltFt != null ? row.eqAltFt : '—',
            row.surface,
            row.surfaceAltM  != null ? row.surfaceAltM  : '—',
            row.surfaceAltFt != null ? row.surfaceAltFt : '—',
            row.differenceM  != null ? row.differenceM  : '—',
            row.differenceFt != null ? row.differenceFt : '—',
            row.hlM  != null ? row.hlM  : '—',
            row.hlFt != null ? row.hlFt : '—',
            row.ocaM  != null ? row.ocaM  : '—',
            row.ocaFt != null ? row.ocaFt : '—',
            row.isCritical ? 'YES' : 'NO',
          ]);

          // Critical column is index 18
          doc.autoTable({
            head: obsHead,
            body: obsBody,
            startY: Math.max(y, contentAreaTop),
            margin: { left: 18, right: 18, top: contentAreaTop, bottom: marginBottom + footerHeight },
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
            showHead: 'everyPage',
            rowPageBreak: 'avoid',
            theme: 'grid',
            didParseCell: (data) => {
              if (data.section === 'body') {
                const critical = obsBody[data.row.index]?.[18];
                if (critical === 'YES') {
                  data.cell.styles.textColor = [183, 28, 28];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            },
          });
          y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
        }
      }
    }
  }

  // GNSS APCH belongs under procedures, alongside the OAS assessments.
  y = appendGnssApproachReport({
    doc, analysis: gnssApchAnalysis, t, startY: y,
    contentAreaTop, marginBottom, footerHeight,
  });

  y = appendLocApproachReport({
    doc, analysis: locAnalysis, t, startY: y, contentAreaTop, marginBottom, footerHeight,
  });

  // VSS / OCS Section
  if (vssAnalysis && vssAnalysis.results && vssAnalysis.results.length > 0) {
    const vssResultsWithObs = vssAnalysis.results.filter(r =>
      (r.vssRows && r.vssRows.length > 0) || (r.ocsRows && r.ocsRows.length > 0)
    );

    if (vssResultsWithObs.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, t("vssResults") || "VSS/OCS Calculation Results", 18, y);
      y += 8;

      for (const result of vssResultsWithObs) {
        // Heading: airport – runway – procedure
        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const vssHeading = [
          result.input.airport_name || result.input.airport_code,
          `RWY ${result.input.runway_designation}`,
          result.input.procedure ? `– ${result.input.procedure}` : null
        ].filter(Boolean).join(' – ');
        y = addTextWithOffsetLocal(doc, vssHeading, 18, y);
        y += 4;

        // Input parameters table
        const vssParamsHead = [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']];
        const ocaStr = result.input.oca_value_ft != null
          ? `${(result.input.oca_value_ft * 0.3048).toFixed(1)} m / ${result.input.oca_value_ft} ft`
          : '—';
        const rdhStr = result.input.rdh_ft != null
          ? `${(result.input.rdh_ft * 0.3048).toFixed(1)} m / ${result.input.rdh_ft} ft`
          : '—';
        const apvBaroStr = result.input.apvbaro
          ? `Yes (min VPA: ${result.input.min_vpa_deg != null ? result.input.min_vpa_deg + '°' : 'N/A'})`
          : 'No';
        const thrPosStr = result.input.thr_lat != null
          ? `${toDMS(result.input.thr_lat, true)} / ${toDMS(result.input.thr_lon, false)}`
          : '—';
        const thrElevStr = result.input.thr_elev_m != null ? `${result.input.thr_elev_m.toFixed(1)} m` : '—';
        const inboundStr = result.input.inbound_track_deg != null
          ? `${parseFloat(result.input.inbound_track_deg).toFixed(2)}° (UTM: ${result.input.utm_grid_bearing_deg != null ? result.input.utm_grid_bearing_deg.toFixed(2) : '—'}°)`
          : '—';
        const rwyWidthStr = result.input.rwy_width_m != null ? `${result.input.rwy_width_m} m` : '—';

        const vssParamsBody = [
          ['Approach Type', result.input.apch_type || '—', 'GPA (°)', result.input.gpa_deg != null ? `${result.input.gpa_deg}°` : '—', 'OCA', ocaStr],
          ['RDH', rdhStr, 'OCS enabled', result.input.ocs ? 'Yes' : 'No', 'APV BARO', apvBaroStr],
          ['Cat F', result.input.cat_f ? 'Yes' : 'No', 'RWY Code', result.input.rwy_code || '—', 'RWY Width', rwyWidthStr],
          ...(result.input.apch_type === 'NonPrecision' ? [[
            'True Inbound (°)',
            result.input.true_inbound_deg != null
              ? `${parseFloat(result.input.true_inbound_deg).toFixed(2)}° (UTM: ${result.input.utm_grid_inbound_deg != null ? result.input.utm_grid_inbound_deg.toFixed(2) : '—'}°)`
              : '—',
            'Strip Width',
            result.input.strip_width_m != null ? `${result.input.strip_width_m} m` : '—',
            '', ''
          ]] : []),
          ['THR Position', thrPosStr, 'THR Elevation', thrElevStr, 'Inbound Track (°)', inboundStr],
        ];

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.autoTable({
          startY: Math.max(y, contentAreaTop),
          head: vssParamsHead,
          body: vssParamsBody,
          styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 32, fontStyle: 'bold' },
            1: { cellWidth: 40 },
            2: { cellWidth: 28, fontStyle: 'bold' },
            3: { cellWidth: 32 },
            4: { cellWidth: 24, fontStyle: 'bold' },
            5: { cellWidth: 'auto' },
          },
          margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
          tableWidth: 'auto',
          showHead: 'firstPage',
          theme: 'grid',
        });
        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 4 : y + 20;

        // Surface geometry line
        if (result.geometry) {
          y = ensureNotInHeaderOrFooter(doc, y, 10);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          let geoLine = `OCH = ${result.geometry.ochM} m  |  Length = ${result.geometry.lengthM} m  |  Half-width = ${result.geometry.halfWidthM} m  |  tan(VSS) = ${result.geometry.tangVSS}`;
          if (result.geometry.tangOCS != null) {
            geoLine += `  |  tan(OCS) = ${result.geometry.tangOCS}`;
            if (result.geometry.ocsBaseElevM != null) geoLine += `  |  OCS base elev = ${result.geometry.ocsBaseElevM} m`;
          }
          if (result.input.apch_type === 'NonPrecision') {
            geoLine += `  |  Diverge L=${result.geometry.leftDiverge} R=${result.geometry.rightDiverge}`;
          }
          const geoLines = doc.splitTextToSize(geoLine, 170);
          y = addTextWithOffsetLocal(doc, geoLines, 18, y);
          y += 6;
        }

        // Helper to build and render a VSS or OCS obstacle table
        function renderVssPdfTable(rows, surface) {
          if (!rows || rows.length === 0) return;
          const isVSS = surface === 'VSS';

          const critRows = rows.filter(r => r.isCritical);
          const nonCritRows = rows.filter(r => !r.isCritical);
          const sortedRows = [...critRows, ...nonCritRows];

          // Sub-heading
          y = ensureNotInHeaderOrFooter(doc, y, 14);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          const critCount = rows.filter(r => r.isCritical).length;
          const subHeading = `${surface} Surface  |  Obstacles: ${rows.length}  |  Critical: ${critCount}`;
          y = addTextWithOffsetLocal(doc, subHeading, 18, y);
          y += 3;

          const obsHead = [[
            t('baroVnavObstName') || 'Obstacle Name',
            t('vssObstAltM') || 'Alt (m)',
            t('vssObstAltFt') || 'Alt (ft)',
            t('baroVnavAlongTrackM') || 'Along-track (m)',
            t('baroVnavSurface') || 'Surface',
            t('baroVnavSurfAltM') || 'Surf. Alt (m)',
            t('baroVnavSurfAltFt') || 'Surf. Alt (ft)',
            t('baroVnavDiffM') || 'Diff (m)',
            t('baroVnavDiffFt') || 'Diff (ft)',
            t('baroVnavCritical') || 'Critical',
            ...(isVSS ? [t('vssDisregardable') || 'Disregardable'] : []),
            t('vssImpact') || 'Impact',
          ]];

          const obsBody = sortedRows.map(row => {
            const hasImpact = row.isCritical && (surface === 'OCS' || !row.isDisregardable);
            return [
              row.obstacleName || '—',
              row.obstacleAltM != null ? row.obstacleAltM : '—',
              row.obstacleAltFt != null ? row.obstacleAltFt : '—',
              row.alongTrackM != null ? row.alongTrackM : '—',
              row.surface || '—',
              row.surfaceAltM != null ? row.surfaceAltM : '—',
              row.surfaceAltFt != null ? row.surfaceAltFt : '—',
              row.differenceM != null ? row.differenceM : '—',
              row.differenceFt != null ? row.differenceFt : '—',
              row.isCritical ? 'YES' : '—',
              ...(isVSS ? [row.isDisregardable ? 'Yes' : 'No'] : []),
              hasImpact ? 'YES' : '—',
            ];
          });

          const critIdx = 9;
          const impactIdx = isVSS ? 11 : 10;

          y = ensureNotInHeaderOrFooter(doc, y, 10);
          doc.autoTable({
            startY: Math.max(y, contentAreaTop),
            head: obsHead,
            body: obsBody,
            styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
            headStyles: {
              fillColor: [32, 114, 184],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 7,
            },
            columnStyles: {
              0: { cellWidth: 24, halign: 'left' },
              1: { cellWidth: 12, halign: 'right' },
              2: { cellWidth: 12, halign: 'right' },
              3: { cellWidth: 16, halign: 'right' },
              4: { cellWidth: 14, halign: 'center' },
              5: { cellWidth: 14, halign: 'right' },
              6: { cellWidth: 14, halign: 'right' },
              7: { cellWidth: 12, halign: 'right' },
              8: { cellWidth: 12, halign: 'right' },
              9: { cellWidth: 12, halign: 'center' },
              ...(isVSS ? { 10: { cellWidth: 16, halign: 'center' }, 11: { cellWidth: 12, halign: 'center' } }
                       : { 10: { cellWidth: 12, halign: 'center' } }),
            },
            margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
            tableWidth: 'wrap',
            showHead: 'everyPage',
            rowPageBreak: 'avoid',
            didParseCell: (data) => {
              if (data.section === 'body') {
                const row = obsBody[data.row.index];
                if (!row) return;
                const isCrit = row[critIdx] === 'YES';
                const hasImpact = row[impactIdx] === 'YES';
                if (hasImpact && (data.column.index === impactIdx || data.column.index === critIdx)) {
                  data.cell.styles.textColor = [183, 28, 28];
                  data.cell.styles.fontStyle = 'bold';
                } else if (isCrit && data.column.index === critIdx) {
                  data.cell.styles.textColor = [183, 28, 28];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            },
            theme: 'grid',
          });
          y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
        }

        // VSS table
        if (result.vssRows && result.vssRows.length > 0) {
          renderVssPdfTable(result.vssRows, 'VSS');
        } else {
          y = ensureNotInHeaderOrFooter(doc, y, 10);
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          y = addTextWithOffsetLocal(doc, 'VSS Surface: No obstacles within VSS surface.', 18, y);
          y += 4;
        }

        // OCS table (only when OCS is enabled)
        if (result.input.ocs) {
          if (result.ocsRows && result.ocsRows.length > 0) {
            renderVssPdfTable(result.ocsRows, 'OCS');
          } else {
            y = ensureNotInHeaderOrFooter(doc, y, 10);
            doc.setFont("helvetica", "italic");
            doc.setFontSize(8);
            y = addTextWithOffsetLocal(doc, 'OCS Surface: No obstacles within OCS surface.', 18, y);
            y += 4;
          }
        }

        y += 4;
      }
    }
  }

  // Base Turn Section – placed right after VSS/OCS
  {
    const hbResults = holdingBaseAnalysis?.results?.filter(r => r.obsRows && r.obsRows.length > 0) ?? [];

    if (hbResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, t("baseTurnResults") || "Base Turn Calculation Results", 18, y);
      y += 6;

      const M_TO_FT = 3.28084;

      for (const result of hbResults) {
        const inp = result.input;
        const airportLabel = [
          inp.airport_name || inp.airport_code,
          (inp.nav_type || inp.nav_ident) ? `– ${[inp.nav_type, inp.nav_ident].filter(Boolean).join(' ')}` : null,
          '(Base Turn)',
        ].filter(Boolean).join(' ');

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, airportLabel, 18, y);
        y += 4;

        // Input parameter table
        const inbTrackStr = inp.inbound_track_deg != null
          ? `${parseFloat(inp.inbound_track_deg).toFixed(2)}° (UTM grid: ${inp.utm_grid_inbound_deg?.toFixed(2)}°)`
          : '—';
        const altStr = inp.altitude_ft != null
          ? `${inp.altitude_ft} ft / ${result.geometry?.holdingAltM ?? '—'} m` : '—';
        const outbStr = inp.outbound_limit_type === 'time'
          ? `${inp.outbound_time_min} min`
          : `${inp.outbound_dme_nm} NM (DME)`;

        const inputBody = [
          [
            'Nav Ident', inp.nav_ident || '—',
            'Nav Type', inp.nav_type || '—',
            'Turn Direction', inp.turn_direction || '—',
          ],
          [
            'Inbound Track', inbTrackStr,
            'Altitude', altStr,
            'IAS', inp.ias_kt != null ? `${inp.ias_kt} kt` : '—',
          ],
          [
            'Outbound Limit', outbStr,
            'ISA delta', inp.isa_delta_c != null ? `${inp.isa_delta_c} deg C` : '—',
            'MOC Primary', result.geometry?.primaryMocM != null ? `${result.geometry.primaryMocM} m` : '—',
          ],
        ];
        if (result.geometry) {
          const g = result.geometry;
          inputBody.push([
            'TAS', g.tas_kt != null ? `${g.tas_kt} kt` : '—',
            'Turn Radius', g.turn_radius_m != null ? `${Number(g.turn_radius_m).toFixed(1)} m` : '—',
            'Sector Angle', g.degrees2 != null ? `${g.degrees2}°` : '—',
          ]);
        }

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 26, fontStyle: 'bold' }, 1: { cellWidth: 34 },
            2: { cellWidth: 26, fontStyle: 'bold' }, 3: { cellWidth: 34 },
            4: { cellWidth: 26, fontStyle: 'bold' }, 5: { cellWidth: 34 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        // Obstacle table
        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)', 'Zone',
          'Dist. from primary bdry (m)', 'MOC (m)', 'MOC (ft)',
          'Surf. Alt (m)', 'Surf. Alt (ft)', 'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.zone || 'Primary',
          row.distFromPrimaryBoundaryM ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.surfaceAltM ?? '—',
          row.surfaceAltFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? 'YES' : '—',
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            3: { cellWidth: 14 },
            11: { cellWidth: 12, halign: 'center' },
          },
          willDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 11 && data.cell.raw === 'YES') {
              data.cell.styles.textColor = [211, 47, 47];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // Holding / Racetrack by Time Section – placed right after Base Turn
  {
    const rtResults = (holdingRacetrackAnalysis?.results?.filter(r => r.obsRows && r.obsRows.length > 0 && r.input.limiting_dme_nm == null)) ?? [];

    if (rtResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "Holding / Racetrack by Time Calculation Results", 18, y);
      y += 6;

      for (const result of rtResults) {
        const inp = result.input;
        const usedFor = inp.used_for_type || 'Holding';
        const airportLabel = [
          inp.airport_name || inp.airport_code,
          (inp.nav_type || inp.nav_ident) ? `– ${[inp.nav_type, inp.nav_ident].filter(Boolean).join(' ')}` : null,
          `(${usedFor})`,
        ].filter(Boolean).join(' ');

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, airportLabel, 18, y);
        y += 4;

        // Input parameter table
        const inbTrackStr = inp.inbound_track_deg != null
          ? `${parseFloat(inp.inbound_track_deg).toFixed(2)}° (UTM grid: ${inp.utm_grid_inbound_deg?.toFixed(2)}°)`
          : '—';
        const altStr = inp.altitude_ft != null
          ? `${inp.altitude_ft} ft / ${result.geometry?.holdingAltM ?? '—'} m` : '—';
        const toleranceLabel = inp.tolerance_type === 'facility'
          ? 'Over-head a Facility'
          : inp.tolerance_type === 'custom' || inp.tolerance_type === 'fix'
            ? 'Fix'
            : inp.tolerance_type || '—';
        const navElevStr = inp.nav_elev_ft != null
          ? `${(inp.nav_elev_ft * 0.3048).toFixed(1)} m / ${inp.nav_elev_ft} ft` : '—';

        const inputBody = [
          [
            'Used For', usedFor,
            'Nav Ident', inp.nav_ident || '—',
            'Nav Type', inp.nav_type || '—',
          ],
          [
            'Turn Direction', inp.turn_direction || '—',
            'Inbound Track', inbTrackStr,
            'Altitude', altStr,
          ],
          [
            'IAS', inp.ias_kt != null ? `${inp.ias_kt} kt` : '—',
            'Outbound Time', inp.outbound_time_min != null ? `${inp.outbound_time_min} min` : '—',
            'ISA delta', inp.isa_delta_c != null ? `${inp.isa_delta_c} deg C` : '—',
          ],
          [
            'Category H', inp.use_category_h ? 'Yes' : 'No',
            'Tolerance Type', toleranceLabel,
            'NAV Elevation', navElevStr,
          ],
        ];
        if (result.geometry) {
          const g = result.geometry;
          inputBody.push([
            'TAS', g.tas_kt != null ? `${g.tas_kt} kt` : '—',
            'Wind', g.wind_kt != null ? `${g.wind_kt} kt` : '—',
            'Turn Radius', g.turn_radius_m != null ? `${Number(g.turn_radius_m).toFixed(1)} m` : '—',
          ]);
        }

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          // Light blue header matching Base Turn style
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 26, fontStyle: 'bold' }, 1: { cellWidth: 34 },
            2: { cellWidth: 26, fontStyle: 'bold' }, 3: { cellWidth: 34 },
            4: { cellWidth: 26, fontStyle: 'bold' }, 5: { cellWidth: 34 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        // Obstacle table
        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)', 'Zone',
          'Dist. from primary bdry (m)', 'MOC (m)', 'MOC (ft)',
          'Surf. Alt (m)', 'Surf. Alt (ft)', 'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.zone || 'Primary',
          row.distFromPrimaryBoundaryM ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.surfaceAltM ?? '—',
          row.surfaceAltFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? 'YES' : '—',
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          // Dark blue header matching Base Turn obstacle table style
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            3: { cellWidth: 14 },
            11: { cellWidth: 12, halign: 'center' },
          },
          willDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 11 && data.cell.raw === 'YES') {
              data.cell.styles.textColor = [211, 47, 47];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // Holding / Racetrack by Distance Section – placed right after by Time section
  {
    const rdResults = (holdingRacetrackAnalysis?.results?.filter(r => r.obsRows && r.obsRows.length > 0 && r.input.limiting_dme_nm != null)) ?? [];

    if (rdResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "Holding / Racetrack by Distance Calculation Results", 18, y);
      y += 6;

      for (const result of rdResults) {
        const inp = result.input;
        const usedFor = inp.used_for_type || 'Holding';
        const airportLabel = [
          inp.airport_name || inp.airport_code,
          (inp.nav_type || inp.nav_ident) ? `– ${[inp.nav_type, inp.nav_ident].filter(Boolean).join(' ')}` : null,
          `(${usedFor})`,
        ].filter(Boolean).join(' ');

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, airportLabel, 18, y);
        y += 4;

        // Input parameter table
        const inbTrackStr = inp.inbound_track_deg != null
          ? `${parseFloat(inp.inbound_track_deg).toFixed(2)}° (UTM grid: ${inp.utm_grid_inbound_deg?.toFixed(2)}°)`
          : '—';
        const altStr = inp.altitude_ft != null
          ? `${inp.altitude_ft} ft / ${result.geometry?.holdingAltM ?? '—'} m` : '—';
        const toleranceLabel = inp.tolerance_type === 'facility'
          ? 'Over-head a Facility'
          : inp.tolerance_type === 'custom' || inp.tolerance_type === 'fix'
            ? 'Fix'
            : inp.tolerance_type || '—';
        const navElevStr = inp.nav_elev_ft != null
          ? `${(inp.nav_elev_ft * 0.3048).toFixed(1)} m / ${inp.nav_elev_ft} ft` : '—';
        const limitingNavPosStr = inp.limiting_nav_lat != null
          ? `${toDMS(inp.limiting_nav_lat, true)} / ${toDMS(inp.limiting_nav_lon, false)}` : '—';

        const inputBody = [
          [
            'Used For', usedFor,
            'Nav Ident', inp.nav_ident || '—',
            'Nav Type', inp.nav_type || '—',
          ],
          [
            'Turn Direction', inp.turn_direction || '—',
            'Inbound Track', inbTrackStr,
            'Altitude', altStr,
          ],
          [
            'IAS', inp.ias_kt != null ? `${inp.ias_kt} kt` : '—',
            'Outbound Distance', inp.limiting_dme_nm != null ? `${inp.limiting_dme_nm} NM` : '—',
            'ISA delta', inp.isa_delta_c != null ? `${inp.isa_delta_c} deg C` : '—',
          ],
          [
            'Limiting Nav Ident', inp.limiting_nav_ident || '—',
            'Limiting Nav Position', limitingNavPosStr,
            'Eff. Outbound Dist', inp.dme_effective_dist_m != null ? `${inp.dme_effective_dist_m} m` : '—',
          ],
          [
            'Category H', inp.use_category_h ? 'Yes' : 'No',
            'Tolerance Type', toleranceLabel,
            'NAV Elevation', navElevStr,
          ],
        ];
        if (result.geometry) {
          const g = result.geometry;
          inputBody.push([
            'TAS', g.tas_kt != null ? `${g.tas_kt} kt` : '—',
            'Wind', g.wind_kt != null ? `${g.wind_kt} kt` : '—',
            'Turn Radius', g.turn_radius_m != null ? `${Number(g.turn_radius_m).toFixed(1)} m` : '—',
          ]);
        }

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 26, fontStyle: 'bold' }, 1: { cellWidth: 34 },
            2: { cellWidth: 26, fontStyle: 'bold' }, 3: { cellWidth: 34 },
            4: { cellWidth: 26, fontStyle: 'bold' }, 5: { cellWidth: 34 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        // Obstacle table
        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)', 'Zone',
          'Dist. from primary bdry (m)', 'MOC (m)', 'MOC (ft)',
          'Surf. Alt (m)', 'Surf. Alt (ft)', 'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.zone || 'Primary',
          row.distFromPrimaryBoundaryM ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.surfaceAltM ?? '—',
          row.surfaceAltFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? 'YES' : '—',
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            3: { cellWidth: 14 },
            11: { cellWidth: 12, halign: 'center' },
          },
          willDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 11 && data.cell.raw === 'YES') {
              data.cell.styles.textColor = [211, 47, 47];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // RNAV Holding Section – placed right after Holding / Racetrack by Time
  {
    const rnavResults = rnavHoldingAnalysis?.results?.filter(r => r.obsRows && r.obsRows.length > 0) ?? [];

    if (rnavResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "RNAV Holding Calculation Results", 18, y);
      y += 6;

      for (const result of rnavResults) {
        const inp = result.input;
        const airportLabel = [
          inp.airport_name || inp.airport_code,
          inp.wpt_ident ? `– WPT: ${inp.wpt_ident}` : null,
          `(RNAV Holding)`,
        ].filter(Boolean).join(' ');

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, airportLabel, 18, y);
        y += 4;

        // Input parameter table
        const inbTrackStr = inp.inbound_track_deg != null
          ? `${parseFloat(inp.inbound_track_deg).toFixed(2)}° (UTM grid: ${inp.utm_grid_inbound_deg?.toFixed(2)}°)`
          : '—';
        const altStr = inp.altitude_ft != null
          ? `${inp.altitude_ft} ft / ${result.geometry?.holdingAltM ?? '—'} m` : '—';

        const inputBody = [
          [
            'WPT Ident', inp.wpt_ident || '—',
            'Outbound Type', inp.outbound_type || 'time',
            'ATT / XTT', `${inp.att_nm ?? 0.8} NM / ${inp.xtt_nm ?? 1.0} NM`,
          ],
          [
            'Turn Direction', inp.turn_direction || '—',
            'Inbound Track', inbTrackStr,
            'Altitude', altStr,
          ],
          [
            'IAS', inp.ias_kt != null ? `${inp.ias_kt} kt` : '—',
            'Outbound Time', inp.outbound_time_min != null ? `${inp.outbound_time_min} min` : '—',
            'ISA delta', inp.isa_delta_c != null ? `${inp.isa_delta_c} deg C` : '—',
          ],
          [
            'Category H', inp.use_category_h ? 'Yes' : 'No',
            'Wind Type', inp.wind_type || '—',
            '', '',
          ],
        ];
        if (result.geometry) {
          const g = result.geometry;
          inputBody.push([
            'TAS', g.tas_kt != null ? `${g.tas_kt} kt` : '—',
            'Wind', g.wind_kt != null ? `${g.wind_kt} kt` : '—',
            'Turn Radius', g.turn_radius_m != null ? `${Number(g.turn_radius_m).toFixed(1)} m` : '—',
          ]);
        }

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 26, fontStyle: 'bold' }, 1: { cellWidth: 34 },
            2: { cellWidth: 26, fontStyle: 'bold' }, 3: { cellWidth: 34 },
            4: { cellWidth: 26, fontStyle: 'bold' }, 5: { cellWidth: 34 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        // Obstacle table
        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)', 'Zone',
          'Dist. from primary bdry (m)', 'MOC (m)', 'MOC (ft)',
          'Surf. Alt (m)', 'Surf. Alt (ft)', 'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.zone || 'Primary',
          row.distFromPrimaryBoundaryM ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.surfaceAltM ?? '—',
          row.surfaceAltFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? 'YES' : '—',
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            3: { cellWidth: 14 },
            11: { cellWidth: 12, halign: 'center' },
          },
          willDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 11 && data.cell.raw === 'YES') {
              data.cell.styles.textColor = [211, 47, 47];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // RNP Holding Section – placed right after RNAV Holding
  {
    const rnpResults = rnpHoldingAnalysis?.results?.filter(r => r.obsRows && r.obsRows.length > 0) ?? [];

    if (rnpResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "RNP Holding Calculation Results", 18, y);
      y += 6;

      for (const result of rnpResults) {
        const inp = result.input;
        const airportLabel = [
          inp.airport_name || inp.airport_code,
          inp.wpt_ident ? `– WPT: ${inp.wpt_ident}` : null,
          `(RNP ${inp.rnp_value || 1.0} Holding)`,
        ].filter(Boolean).join(' ');

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, airportLabel, 18, y);
        y += 4;

        // Input parameter table
        const inbTrackStr = inp.inbound_track_deg != null
          ? `${parseFloat(inp.inbound_track_deg).toFixed(2)}° (UTM grid: ${inp.utm_grid_inbound_deg?.toFixed(2)}°)`
          : '—';
        const altStr = inp.altitude_ft != null
          ? `${inp.altitude_ft} ft / ${result.geometry?.holdingAltM ?? '—'} m` : '—';

        const inputBody = [
          [
            'WPT Ident', inp.wpt_ident || '—',
            'RNP Value', `${inp.rnp_value || 1.0} NM`,
            'Turn Direction', inp.turn_direction || '—',
          ],
          [
            'Inbound Track', inbTrackStr,
            'Altitude', altStr,
            'IAS', inp.ias_kt != null ? `${inp.ias_kt} kt` : '—',
          ],
          [
            'Outbound Time', inp.outbound_time_min != null ? `${inp.outbound_time_min} min` : '—',
            'ISA delta', inp.isa_delta_c != null ? `${inp.isa_delta_c} deg C` : '—',
            'Category H', inp.use_category_h ? 'Yes' : 'No',
          ],
          [
            'Wind Type', inp.wind_type || '—',
            '', '',
            '', '',
          ],
        ];
        if (result.geometry) {
          const g = result.geometry;
          inputBody.push([
            'TAS', g.tas_kt != null ? `${g.tas_kt} kt` : '—',
            'Wind', g.wind_kt != null ? `${g.wind_kt} kt` : '—',
            'Turn Radius', g.turn_radius_m != null ? `${Number(g.turn_radius_m).toFixed(1)} m` : '—',
          ]);
        }

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 26, fontStyle: 'bold' }, 1: { cellWidth: 34 },
            2: { cellWidth: 26, fontStyle: 'bold' }, 3: { cellWidth: 34 },
            4: { cellWidth: 26, fontStyle: 'bold' }, 5: { cellWidth: 34 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        // Obstacle table
        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)', 'Zone',
          'Dist. from primary bdry (m)', 'MOC (m)', 'MOC (ft)',
          'Surf. Alt (m)', 'Surf. Alt (ft)', 'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.zone || 'Primary',
          row.distFromPrimaryBoundaryM ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.surfaceAltM ?? '—',
          row.surfaceAltFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? 'YES' : '—',
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            3: { cellWidth: 14 },
            11: { cellWidth: 12, halign: 'center' },
          },
          willDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 11 && data.cell.raw === 'YES') {
              data.cell.styles.textColor = [211, 47, 47];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // MSA (Minimum Sector Altitude) Section
  {
    const msaResults = msaAnalysis?.results?.filter(r => !r.input.error && r.obsRows && r.obsRows.length > 0) ?? [];

    if (msaResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "MSA (Minimum Sector Altitude) Calculation Results", 18, y);
      y += 6;

      for (const result of msaResults) {
        const inp = result.input;
        const sectorLabel = `${inp.airport_name || inp.airport_code || '—'}${inp.runway_designation ? ` RWY ${inp.runway_designation}` : ''} – MSA ${inp.sector_from_deg != null ? `${inp.sector_from_deg}°` : ''}–${inp.sector_to_deg != null ? `${inp.sector_to_deg}°` : ''}`;

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, sectorLabel, 18, y);
        y += 4;

        // Input parameter table
        const toDmsStr = (dec, isLat) => {
          if (dec == null) return '—';
          const abs = Math.abs(dec);
          const deg = Math.floor(abs);
          const minF = (abs - deg) * 60;
          const min = Math.floor(minF);
          const sec = (minF - min) * 60;
          const dir = isLat ? (dec >= 0 ? 'N' : 'S') : (dec >= 0 ? 'E' : 'W');
          return String(deg).padStart(isLat ? 2 : 3, '0') + String(min).padStart(2, '0') + sec.toFixed(2).padStart(5, '0') + dir;
        };
        const centreStr = inp.centre_lat != null
          ? `${toDmsStr(inp.centre_lat, true)} / ${toDmsStr(inp.centre_lon, false)}`
          : '—';
        const pubMsa = inp.altitude != null ? `${inp.altitude} ${inp.altitude_unit || ''}` : '—';
        const calcMsa = result.msa?.formatted || '—';

        const inputBody = [
          [
            'MSA Reference', inp.msa_id || '—',
            'Nav Aid', inp.nav_aid_name || '—',
            'Radius', inp.radius_nm != null ? `${inp.radius_nm} NM` : '—',
          ],
          [
            'Sector From', inp.sector_from_deg != null ? `${inp.sector_from_deg}°` : '—',
            'Sector To', inp.sector_to_deg != null ? `${inp.sector_to_deg}°` : '—',
            'MOC', inp.moc != null ? `${inp.moc} ${inp.moc_unit || ''}` : '—',
          ],
          [
            'Centre', centreStr,
            'Published MSA', pubMsa,
            'Calculated MSA', calcMsa,
          ],
        ];

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 28, fontStyle: 'bold' }, 1: { cellWidth: 38 },
            2: { cellWidth: 28, fontStyle: 'bold' }, 3: { cellWidth: 38 },
            4: { cellWidth: 28, fontStyle: 'bold' }, 5: { cellWidth: 20 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        // Obstacle table
        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)',
          'MOC (m)', 'MOC (ft)', 'OCA (m)', 'OCA (ft)',
          'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.ocaM ?? '—',
          row.ocaFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? (t('language') === 'Svenska' ? 'Ja' : 'Yes') : (t('language') === 'Svenska' ? 'Nej' : 'No'),
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            9: { cellWidth: 12, halign: 'center' },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 9) {
              const val = data.cell.raw;
              if (val === 'Yes' || val === 'Ja') {
                data.cell.styles.textColor = [211, 47, 47];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // TAA (Terminal Arrival Altitude) Section
  {
    const taaResults = taaAnalysis?.results?.filter(r => !r.input.error && r.obsRows && r.obsRows.length > 0) ?? [];

    if (taaResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "TAA (Terminal Arrival Altitude) Calculation Results", 18, y);
      y += 6;

      const toDmsStr = (dec, isLat) => {
        if (dec == null) return '—';
        const abs = Math.abs(dec);
        const deg = Math.floor(abs);
        const minF = (abs - deg) * 60;
        const min = Math.floor(minF);
        const sec = (minF - min) * 60;
        const dir = isLat ? (dec >= 0 ? 'N' : 'S') : (dec >= 0 ? 'E' : 'W');
        return String(deg).padStart(isLat ? 2 : 3, '0') + String(min).padStart(2, '0') + sec.toFixed(2).padStart(5, '0') + dir;
      };

      for (const result of taaResults) {
        const inp = result.input;
        const sectorLabel = `${inp.airport_name || inp.airport_code || '—'} – TAA ${inp.sector_type || ''} (IF: ${inp.center_wpt_ident || '—'}) ${inp.sector_from_deg != null ? `${inp.sector_from_deg}°` : ''}–${inp.sector_to_deg != null ? `${inp.sector_to_deg}°` : ''}`;

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, sectorLabel, 18, y);
        y += 4;

        const centreStr = inp.centre_lat != null
          ? `${toDmsStr(inp.centre_lat, true)} / ${toDmsStr(inp.centre_lon, false)}`
          : '—';
        const pubTaa = inp.min_altitude != null ? `${inp.min_altitude} ${inp.altitude_unit || ''}` : '—';
        const calcTaa = result.taa?.formatted || '—';

        const inputBody = [
          [
            'IF Waypoint', inp.center_wpt_ident || '—',
            'Sector Type', inp.sector_type || '—',
            'Radius', inp.radius_nm != null ? `${inp.radius_nm} NM` : '—',
          ],
          [
            'Sector From', inp.sector_from_deg != null ? `${inp.sector_from_deg}°` : '—',
            'Sector To', inp.sector_to_deg != null ? `${inp.sector_to_deg}°` : '—',
            'MOC', inp.moc != null ? `${inp.moc} ${inp.moc_unit || ''}` : '—',
          ],
          [
            'Centre', centreStr,
            'Published TAA', pubTaa,
            'Calculated TAA', calcTaa,
          ],
        ];

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 28, fontStyle: 'bold' }, 1: { cellWidth: 38 },
            2: { cellWidth: 28, fontStyle: 'bold' }, 3: { cellWidth: 38 },
            4: { cellWidth: 28, fontStyle: 'bold' }, 5: { cellWidth: 20 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)',
          'MOC (m)', 'MOC (ft)', 'OCA (m)', 'OCA (ft)',
          'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.ocaM ?? '—',
          row.ocaFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? (t('language') === 'Svenska' ? 'Ja' : 'Yes') : (t('language') === 'Svenska' ? 'Nej' : 'No'),
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            9: { cellWidth: 12, halign: 'center' },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 9) {
              const val = data.cell.raw;
              if (val === 'Yes' || val === 'Ja') {
                data.cell.styles.textColor = [211, 47, 47];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // Circling Section
  {
    const circResults = circlingAnalysis?.results?.filter(r => !r.input?.error && r.obsRows && r.obsRows.length > 0) ?? [];

    if (circResults.length > 0) {
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      y = addTextWithOffsetLocal(doc, "Circling Calculation Results", 18, y);
      y += 6;

      for (const result of circResults) {
        const inp = result.input;
        const catLabel = inp.category != null
          ? `Cat ${inp.category === 'custom' ? 'Custom' : inp.category.toUpperCase()}`
          : '';
        const rwys = inp.runway_designations?.length > 0 ? inp.runway_designations.join(', ') : (inp.runway_designation || '—');
        const areaLabel = `${inp.airport_code || '—'} – ${catLabel}${inp.procedure_id ? ` / ${inp.procedure_id}` : ''} / RWY ${rwys}${inp.circling_restriction ? ` (${inp.circling_restriction})` : ''}`;

        y = ensureNotInHeaderOrFooter(doc, y, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        y = addTextWithOffsetLocal(doc, areaLabel, 18, y);
        y += 4;

        const pubMin  = inp.stored_alt_ft != null ? `${inp.stored_alt_ft} ft` : '—';
        const calcMin = result.minimum?.formatted || '—';

        const inputBody = [
          [
            'Category',  catLabel,
            'IAS',       inp.ias_kts != null ? `${inp.ias_kts} kts` : '—',
            'TAS (+25)', inp.tas_kts != null ? `${inp.tas_kts} kts` : '—',
          ],
          [
            'Radius',    inp.radius_nm != null ? `${inp.radius_nm} NM (${inp.radius_m} m)` : '—',
            'MOC',       inp.moc_m    != null ? `${inp.moc_m} m / ${inp.moc_ft} ft` : '—',
            'Restriction', inp.circling_restriction || 'None',
          ],
          [
            'AD Elevation', inp.ad_elev_ft != null ? `${inp.ad_elev_ft} ft` : '—',
            'Published Min', pubMin,
            'Computed Min',  calcMin,
          ],
        ];

        doc.autoTable({
          startY: y,
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: inputBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 28, fontStyle: 'bold' }, 1: { cellWidth: 38 },
            2: { cellWidth: 28, fontStyle: 'bold' }, 3: { cellWidth: 38 },
            4: { cellWidth: 28, fontStyle: 'bold' }, 5: { cellWidth: 20 },
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 4;

        const criticalCount = result.criticalCount ?? 0;
        const obsSubtitle = criticalCount > 0
          ? `Obstacles inside protected area – ${criticalCount} critical`
          : 'Obstacles inside protected area – no critical obstacles';

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        y = addTextWithOffsetLocal(doc, obsSubtitle, 18, y);
        y += 2;

        const obsHead = [[
          'Obstacle Name', 'Alt (m)', 'Alt (ft)',
          'MOC (m)', 'MOC (ft)', 'OCA (m)', 'OCA (ft)',
          'Clearance (m)', 'Clearance (ft)', 'Critical',
        ]];
        const obsBody = result.obsRows.map(row => [
          row.obstacleName || '—',
          row.obstacleAltM ?? '—',
          row.obstacleAltFt ?? '—',
          row.mocM != null ? `${row.mocM} m` : '—',
          row.mocFt != null ? `${row.mocFt} ft` : '—',
          row.ocaM ?? '—',
          row.ocaFt ?? '—',
          row.clearanceM ?? '—',
          row.clearanceFt ?? '—',
          row.isCritical ? (t('language') === 'Svenska' ? 'Ja' : 'Yes') : (t('language') === 'Svenska' ? 'Nej' : 'No'),
        ]);

        doc.autoTable({
          startY: y,
          head: obsHead,
          body: obsBody,
          theme: 'striped',
          styles: { fontSize: 6.5, cellPadding: 2 },
          headStyles: { fillColor: [32, 114, 184], textColor: [255, 255, 255], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 32 },
            9: { cellWidth: 12, halign: 'center' },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 9) {
              const val = data.cell.raw;
              if (val === 'Yes' || val === 'Ja') {
                data.cell.styles.textColor = [211, 47, 47];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          margin: { top: marginTop + headerHeight, bottom: marginBottom + footerHeight, left: 18, right: 10 },
          didDrawPage: () => {},
        });
        y = doc.lastAutoTable.finalY + 8;
      }
    }
  }

  // Pre-compute whether there are shielding obstacles to show
  const shieldingResultsRaw = shieldingAnalysis?.results?.filter(r => r.rowsWithinRadius?.length > 0) ?? [];
  // Expand VOR/DME into two virtual results – one for VOR rows, one for DME_N rows
  const shieldingResultsWithObs = shieldingResultsRaw.flatMap(result => {
    if (result.input.nav_aid_type !== 'VOR/DME') return [result];
    return ['VOR', 'DME_N'].map(type => ({
      ...result,
      input: { ...result.input, nav_aid_type: type },
      rowsWithinRadius: result.rowsWithinRadius.filter(r => r.navAidType === type),
      rows: (result.rows || []).filter(r => r.navAidType === type),
    })).filter(sub => sub.rowsWithinRadius.length > 0);
  });
  const hasShieldingObstacles = shieldingResultsWithObs.length > 0;

  y = addTitleWithOffsetLocal(doc, t("impactOnTechnicalEquipment"), 14, y);
  y += 10;
  if (!hasShieldingObstacles) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addTextWithOffsetLocal(doc, t("noImpact"), 18, y);
    y += 10;
  }

  // Shielding / Nav Aid obstacle tables – placed under impactOnTechnicalEquipment
  if (hasShieldingObstacles) {
    const M_TO_FT = 3.28084;
    const SHIELDING_AREA_LABELS = {
      ground:   t('shieldingZoneGround')   || 'Ground',
      lateral:  t('shieldingZoneLateral')  || 'Lateral',
      sloping:  t('shieldingZoneSloping')  || 'Sloping',
      inner:    t('shieldingZoneInner')     || 'Inner',
      cone:     t('shieldingZoneCone')      || 'Cone',
      extended: t('shieldingZoneExtended')  || 'Extended',
    };

    y = ensureNotInHeaderOrFooter(doc, y, 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    y = addTextWithOffsetLocal(doc, t('shieldingNavAidTitle') || 'Shielding – Nav Aid Obstacle Analysis', 18, y);
    y += 8;

    for (const result of shieldingResultsWithObs) {
      const inp = result.input;
      const navAidTypeLabel = getNavAidTypeLabel(inp.nav_aid_type) || '—';

      // Section heading
      y = ensureNotInHeaderOrFooter(doc, y, 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const shieldingHeading = [
        inp.airport_name || inp.airport_code,
        inp.runway_designation ? `RWY ${inp.runway_designation}` : null,
        inp.nav_aid_name || inp.nav_ident || navAidTypeLabel,
        `(${navAidTypeLabel})`,
      ].filter(Boolean).join(' – ');
      y = addTextWithOffsetLocal(doc, shieldingHeading, 18, y);
      y += 4;

      // Input Parameters table
      {
        const navElevStr = inp.nav_elev_ft != null
          ? `${(inp.nav_elev_ft * 0.3048).toFixed(1)} m / ${inp.nav_elev_ft} ft` : '—';
        const navPosStr = (inp.nav_position_lat != null && inp.nav_position_long != null)
          ? `${inp.nav_position_lat}  ${inp.nav_position_long}` : '—';

        const navInputBody = [
          [
            t('airport') || 'Airport Code', inp.airport_code || '—',
            t('airport') || 'Airport Name', inp.airport_name || '—',
            t('runway') || 'Runway', inp.runway_designation || '—',
          ],
          [
            t('shieldingNavAidType') || 'Nav Aid Type', navAidTypeLabel,
            t('shieldingNavAidName') || 'Nav Aid Name', inp.nav_aid_name || '—',
            t('shieldingIdent') || 'Ident', inp.nav_ident || '—',
          ],
          [
            t('shieldingFrequency') || 'Frequency', inp.nav_freq || '—',
            t('shieldingChannel') || 'Channel', inp.nav_channel || '—',
            t('shieldingElevation') || 'Elevation', navElevStr,
          ],
          [
            t('shieldingNavAidPosition') || 'Nav Aid Position', navPosStr,
            t('shieldingEarthCurvature') || 'Earth Curvature', inp.earth_curvature ? 'Yes' : 'No',
            '', '',
          ],
        ];
        if (inp.true_track_deg != null || inp.dist_to_thr_m != null) {
          const trueTrackStr = inp.true_track_deg != null
            ? `${parseFloat(inp.true_track_deg).toFixed(2)}° (UTM: ${inp.utm_grid_track_deg != null ? inp.utm_grid_track_deg.toFixed(2) : '—'}°)`
            : '—';
          const distStr = inp.dist_to_thr_m != null
            ? `${parseFloat(inp.dist_to_thr_m).toFixed(1)} m` : '—';
          navInputBody.push([
            t('shieldingTrueTrack') || 'True Track (°)', trueTrackStr,
            t('shieldingDistToThr') || 'Dist to THR', distStr,
            t('shieldingRemarks') || 'Remarks', inp.nav_remarks || '—',
          ]);
        }

        y = ensureNotInHeaderOrFooter(doc, y, 10);
        doc.autoTable({
          startY: Math.max(y, contentAreaTop),
          head: [['Parameter', 'Value', 'Parameter', 'Value', 'Parameter', 'Value']],
          body: navInputBody,
          styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
          headStyles: { fillColor: [100, 181, 246], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 30, fontStyle: 'bold' },
            1: { cellWidth: 40 },
            2: { cellWidth: 28, fontStyle: 'bold' },
            3: { cellWidth: 36 },
            4: { cellWidth: 22, fontStyle: 'bold' },
            5: { cellWidth: 'auto' },
          },
          margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
          tableWidth: 'auto',
          showHead: 'firstPage',
          theme: 'grid',
        });
        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 4 : y + 20;
      }

      // Sort: critical first, then by heightDeltaM descending
      const sortedRows = [...result.rowsWithinRadius].sort((a, b) => {
        if (b.isCritical !== a.isCritical) return b.isCritical ? 1 : -1;
        return (b.heightDeltaM ?? -Infinity) - (a.heightDeltaM ?? -Infinity);
      });

      const criticalCount = sortedRows.filter(r => r.isCritical).length;
      const subHeading = `${t('shieldingWithinSurface') || 'Within protected surface'}: ${sortedRows.length}  |  ${t('shieldingColCritical') || 'Critical'}: ${criticalCount}`;
      y = ensureNotInHeaderOrFooter(doc, y, 10);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      y = addTextWithOffsetLocal(doc, subHeading, 18, y);
      y += 2;

      const obsHead = [[
        t('shieldingColObstacleName') || 'Obstacle Name',
        t('shieldingColAltM')         || 'Alt (m)',
        t('shieldingColAltFt')        || 'Alt (ft)',
        t('shieldingColSurface')      || 'Surface',
        t('shieldingColSurfAltM')     || 'Surf. Alt (m)',
        t('shieldingColSurfAltFt')    || 'Surf. Alt (ft)',
        t('shieldingColCurvCorr')     || 'Curvature Corr. (m)',
        t('shieldingColDiffM')        || 'Diff (m)',
        t('shieldingColDiffFt')       || 'Diff (ft)',
        t('shieldingColCritical')     || 'Critical',
      ]];

      const obsBody = sortedRows.map(row => {
        const altM  = row.obstacleElevM != null ? row.obstacleElevM.toFixed(1)             : '—';
        const altFt = row.obstacleElevM != null ? (row.obstacleElevM * M_TO_FT).toFixed(1) : '—';
        const surfAltM = row.surfaceAltM ?? row.navAidElevM;
        const surfAltMStr  = surfAltM != null ? surfAltM.toFixed(1)             : '—';
        const surfAltFtStr = surfAltM != null ? (surfAltM * M_TO_FT).toFixed(1) : '—';
        const curvStr = (row.applyEarthCurv && row.earthCurvCorrM != null)
          ? row.earthCurvCorrM.toFixed(3)
          : (row.areaName != null ? '0.000' : '—');
        const diffM  = row.heightDeltaM != null ? row.heightDeltaM.toFixed(1)             : '—';
        const diffFt = row.heightDeltaM != null ? (row.heightDeltaM * M_TO_FT).toFixed(1) : '—';
        return [
          row.obstacleName || '—',
          altM, altFt,
          SHIELDING_AREA_LABELS[row.areaName] || row.navAidTypeLabel || row.navAidType || '—',
          surfAltMStr, surfAltFtStr,
          curvStr,
          diffM, diffFt,
          row.isCritical ? 'YES' : 'NO',
        ];
      });

      y = ensureNotInHeaderOrFooter(doc, y, 10);
      doc.autoTable({
        startY: Math.max(y, contentAreaTop),
        head: obsHead,
        body: obsBody,
        styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: {
          fillColor: [32, 114, 184],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 7,
        },
        columnStyles: {
          0: { cellWidth: 26, halign: 'left' },
          1: { cellWidth: 12, halign: 'right' },
          2: { cellWidth: 12, halign: 'right' },
          3: { cellWidth: 18, halign: 'center' },
          4: { cellWidth: 15, halign: 'right' },
          5: { cellWidth: 15, halign: 'right' },
          6: { cellWidth: 18, halign: 'right' },
          7: { cellWidth: 12, halign: 'right' },
          8: { cellWidth: 12, halign: 'right' },
          9: { cellWidth: 14, halign: 'center' },
        },
        margin: { top: contentAreaTop, bottom: marginBottom + footerHeight, left: 18, right: 18 },
        tableWidth: 'wrap',
        showHead: 'everyPage',
        rowPageBreak: 'avoid',
        didParseCell: (data) => {
          if (data.section === 'body') {
            const row = obsBody[data.row.index];
            if (!row) return;
            if (row[9] === 'YES') {
              data.cell.styles.textColor = [183, 28, 28];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
        theme: 'grid',
      });
      y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y + 20;
    }
    y += 4;

    // Add penetration note after shielding tables when any obstacle is critical
    const hasCriticalShielding = shieldingResultsWithObs.some(result =>
      (result.rowsWithinRadius || []).some(r => r.isCritical)
    );
    if (hasCriticalShielding) {
      const noteText = t("shieldingPenetrationNote") ||
        "The object penetrates airport technical equipment surfaces in accordance with ICAO EUR Doc 015. Although the surface is not height-limiting, the guidance indicates that obstacles that do penetrate the surfaces require more detailed analysis to determine the nature of any interference with CNS signals.";
      const splitNote = doc.splitTextToSize(noteText, 170);
      y = ensureNotInHeaderOrFooter(doc, y, splitNote.length * 6 + 10);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      y = addTextWithOffsetLocal(doc, splitNote, 18, y);
      y += 6;
    }
  }

  // Viktig info & Rekommendation
  const importantSections = [
    {
      title: t("importantInformation"),
      text: t("importantInfoText"),
      spacingAfter: 7
    },
    {
      title: t("recommendation"),
      text: t("recommendationText"),
      spacingAfter: 7
    }
  ];
  for (const section of importantSections) {
    y = addTitleWithOffsetLocal(doc, section.title, 14, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const splitTxt = doc.splitTextToSize(section.text, 180);
    for (const line of splitTxt) {
      y = addTextWithOffsetLocal(doc, line, 14, y);
      y += 5;
    }
    y += section.spacingAfter;
  }

  // Adressuppgifter
  y = addTitleWithOffsetLocal(doc, t("airportAddresses"), 14, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  for (const airport of airportsList) {
    y = ensureNotInHeaderOrFooter(doc, y, 60);
    y = addTextWithOffsetLocal(doc, `${airport.label}:`, 18, y);
    y += 6;
    let adr = getAddressForAirport(
      airport.code || airport.name,
      airportAddresses || []
    );
    if (adr) {
      const addressLines = [
        `${t("operator")}: ${adr.operator_name}`,
        `${t("address")}: ${adr.street_address}, ${adr.postal_code} ${adr.city}, ${adr.country}`,
        `${t("phone")}: ${adr.telephone}`,
        ...(adr.telefax ? [`${t("fax")}: ${adr.telefax}`] : []),
        ...(adr.afs ? [`AFS: ${adr.afs}`] : []),
        ...(adr.email ? [`${t("email")}: ${adr.email}`] : []),
        ...(adr.website ? [`${t("website")}: ${adr.website}`] : [])
      ];
      for (const line of addressLines) {
        y = addTextWithOffsetLocal(doc, line, 22, y);
        y += 6;
      }
    } else {
      y = addTextWithOffsetLocal(doc, t("noAddressRegistered"), 22, y);
      y += 6;
    }
    y += 12;
  }

  // Header & Footer på alla sidor
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addHeader(doc, i, totalPages, { showTitle: i === 1 });
    addFooter(doc, i, totalPages);
  }

  // Spara PDF
  const pdfBlob = doc.output("blob");
  const filename =
    t("language") === "Svenska"
      ? "flyghinderanalys.pdf"
      : "obstacle-analysis.pdf";

  await savePdfBlob(pdfBlob, filename, {
    userGestureTs,
    gestureTimeoutMs: 10000
  });

  } finally {
  // Restore the user's layers and leave PDF mode even if capture/saving fails.
  setShowSurfaces(prevLayers.showSurfaces);
  setShowEasa(prevLayers.showEasa);
  setShowAnnex14 && setShowAnnex14(prevLayers.showAnnex14);
  setShowEtod && setShowEtod(prevLayers.showEtod);
  setShowPansOps(prevLayers.showPansOps);
  setShowGeoJson(prevLayers.showGeoJson);
  setShowSwedishAirports(prevLayers.showSwedishAirports);
  setShowNavaids(prevLayers.showNavaids);
  setShowNav(prevLayers.showNav);
  setShowTmaTia(prevLayers.showTmaTia);
  setShowCtrTizRmz(prevLayers.showCtrTizRmz);
  setShowUserData(prevLayers.showUserData);
  setPdfMode(false);
  }
}
